// src/components/CostingTable.jsx
import { postCosting as apiPost } from "../utils/costingApi";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Popover,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider,
  Grid,
  TableContainer,
  Paper,
  Drawer,
  Tabs,
  Tab,
} from "@mui/material";

import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";

import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import RefreshIcon from "@mui/icons-material/Refresh";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import CloseIcon from "@mui/icons-material/Close";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import DownloadIcon from "@mui/icons-material/Download";

import { createTheme, ThemeProvider } from "@mui/material/styles";
import "@fontsource/montserrat";

import { useAuth } from "./AuthContext"; // adjust path if needed

const theme = createTheme({
  typography: { fontFamily: "Montserrat, sans-serif" },
});

const cornflowerBlue = "#6495ED";

const BACKEND = "/api/costing";

const COST_SHEET_LIST_FIELDS = [
  "Cost Sheet ID",
  "Timestamp",
  "Owner",
  "Linked Entity Type",
  "Linked Entity ID",
  "Linked Entity Name",
  "Client Name",
  "Project Type",
  "Status",
  "Grand Total",
  "Last Calculated At",
  "Notes",
];
const COST_SHEET_OVERVIEW_FIELDS = [
  "Cost Sheet ID", "Linked Entity Name", "Client Name", "Project Type",
  "Status", "Grand Total", "Last Calculated At",
];

const COST_SHEET_RENDER_LIMIT = 200;
const LINE_ITEM_RENDER_LIMIT = 100;

const COST_SHEET_UPDATED_FIELDS = [
  "Last Updated At",
  "Last Updated",
  "Updated At",
  "Modified At",
  "Last Modified At",
  "Last Calculated At",
  "Timestamp",
];

/* ===================== helpers ===================== */

function safeNum(v) {
  const n = Number(String(v ?? "").replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function fmtINR(n) {
  try {
    return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(n);
  } catch {
    return String(n);
  }
}

function parseDateValue(v) {
  if (v === null || v === undefined || v === "") return null;
  if (v instanceof Date && !isNaN(v.getTime())) return v;

  const s = String(v).trim();
  if (!s) return null;

  const parsed = new Date(s);
  if (!isNaN(parsed.getTime())) return parsed;

  const dmy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+.*)?$/);
  if (dmy) {
    const d = new Date(Number(dmy[3]), Number(dmy[2]) - 1, Number(dmy[1]));
    return isNaN(d.getTime()) ? null : d;
  }

  return null;
}

function getDateTimeMs(v) {
  const d = parseDateValue(v);
  return d ? d.getTime() : 0;
}

function isDateColumn(column) {
  const c = String(column || "").toLowerCase();
  return c === "timestamp" || c.includes(" date") || c.endsWith("date") || c.includes(" at");
}

function formatDateCell(column, value) {
  if (!isDateColumn(column)) return String(value ?? "");
  const d = parseDateValue(value);
  if (!d) return String(value ?? "");

  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

function getCostSheetUpdatedMs(row) {
  for (const field of COST_SHEET_UPDATED_FIELDS) {
    const ms = getDateTimeMs(row?.[field]);
    if (ms) return ms;
  }
  return 0;
}

/**
 * Same-origin Costing API reader.
 */
async function jsonpGet(url) {
  const response = await fetch(url, { method: "GET" });
  const data = await response.json();
  if (!response.ok) throw new Error(data?.error || `Costing request failed (${response.status})`);
  return data;
}

/**
 * ✅ AUTO CALC FIX (single source of truth)
 *
 * Rules:
 * 1) If QTY and Rate are BOTH present (>0) and user chooses Auto => Amount = QTY×Rate (read-only)
 * 2) If QTY×Rate is NOT available => user can manually enter Total Amount (editable)
 *    - If GST% is present, we back-calc:
 *        GST Amount = Total * (GST% / (100 + GST%))
 *        Amount = Total - GST Amount
 *    - If GST% is blank/0, Amount = Total, GST Amount = 0
 * 3) If user manually enters Amount (Auto off), Total auto-computes (Amount + GST Amount)
 */
function recomputeDraftLive(next) {
  const qty = safeNum(next["QTY"]);
  const rate = safeNum(next["Rate"]);
  const hasQtyRate = qty > 0 && rate > 0;

  const gstPct = safeNum(next["GST %"]);

  // Explicit toggles (persisted in draft only, stripped before POST)
  const useAutoAmount = hasQtyRate ? next.__useAutoAmount !== false : false; // default true when qty/rate exists
  const useManualTotal = !hasQtyRate ? Boolean(next.__useManualTotal) : false; // only allowed when no qty/rate

  // Current raw values
  const rawAmountStr = String(next["Amount"] ?? "").trim();
  const rawTotalStr = String(next["Total Amount"] ?? "").trim();

  // 1) Determine Amount
  let amount = next["Amount"];
  if (hasQtyRate && useAutoAmount) {
    amount = qty * rate;
  } else {
    // keep manual amount as typed
    amount = rawAmountStr === "" ? "" : safeNum(amount);
  }

  // 2) If manual total is enabled (only when qty/rate unavailable)
  //    then Total is editable and Amount/GST Amount are derived from Total + GST%
  let gstAmount = "";
  let total = "";

  if (!hasQtyRate && useManualTotal) {
    total = rawTotalStr === "" ? "" : safeNum(next["Total Amount"]);

    if (String(total ?? "").trim() !== "") {
      if (gstPct > 0) {
        const derivedGst = (safeNum(total) * gstPct) / (100 + gstPct);
        const derivedAmt = safeNum(total) - derivedGst;

        amount = derivedAmt;
        gstAmount = derivedGst;
      } else {
        amount = safeNum(total);
        gstAmount = 0;
      }
    } else {
      // if user cleared total, clear derived fields too
      amount = "";
      gstAmount = "";
    }
  } else {
    // 3) Normal mode: GST Amount + Total are computed from Amount and GST%
    const hasAmount = String(amount ?? "").trim() !== "";
    gstAmount = hasAmount ? (safeNum(amount) * gstPct) / 100 : "";
    total = hasAmount ? safeNum(amount) + safeNum(gstAmount) : "";
  }

  // Normalize blanks
  const out = {
    ...next,
    Amount: amount === 0 ? "" : amount,
    "GST Amount": gstAmount === 0 ? "" : gstAmount,
    "Total Amount": total === 0 ? "" : total,

    // UI flags
    __hasQtyRate: hasQtyRate,
    __useAutoAmount: hasQtyRate ? useAutoAmount : false,
    __useManualTotal: !hasQtyRate ? useManualTotal : false,
  };

  return out;
}

/**
 * Final totals before save (server row):
 * - Uses the same rules, but WITHOUT draft-only flags in the payload.
 */
function computeRowTotals(row) {
  const draft = recomputeDraftLive({
    ...row,
    __useAutoAmount: row.__useAutoAmount,
    __useManualTotal: row.__useManualTotal,
  });

  return {
    ...row,
    Amount: draft.Amount ?? "",
    "GST Amount": draft["GST Amount"] ?? "",
    "Total Amount": draft["Total Amount"] ?? "",
  };
}

const DEFAULT_PAYMENT_STATUSES = ["pending", "partially paid", "paid", "hold/disputed"];

function normalizeValidationResponse(raw) {
  const v = raw?.data ? raw.data : raw;

  const headsDirect = Array.isArray(v?.heads) ? v.heads : null;
  const subcatsDirect =
    v?.subcategories && typeof v.subcategories === "object" ? v.subcategories : null;
  const payDirect = Array.isArray(v?.paymentStatus) ? v.paymentStatus : null;

  if (headsDirect || subcatsDirect || payDirect) {
    const pay = (payDirect && payDirect.length ? payDirect : DEFAULT_PAYMENT_STATUSES).map((x) =>
      String(x).trim()
    );
    return {
      heads: headsDirect || [],
      subcategories: subcatsDirect || {},
      paymentStatus: pay,
    };
  }

  const heads =
    (Array.isArray(v?.costHeads) && v.costHeads) ||
    (Array.isArray(v?.["Cost Heads"]) && v["Cost Heads"]) ||
    [];

  const paymentStatusRaw =
    (Array.isArray(v?.["Payment Status"]) && v["Payment Status"]) ||
    (Array.isArray(v?.payment_status) && v.payment_status) ||
    [];

  const paymentStatus =
    paymentStatusRaw && paymentStatusRaw.length
      ? paymentStatusRaw.map((x) => String(x).trim())
      : DEFAULT_PAYMENT_STATUSES;

  const subcategories =
    (v?.subcategoryMap && typeof v.subcategoryMap === "object" && v.subcategoryMap) || {};

  return { heads, subcategories, paymentStatus };
}

function getExtractColsStorageKey(userKey) {
  return `kk_costing_extract_cols_v1::${userKey || "anon"}`;
}

function safeJsonParse(s, fallback) {
  try {
    const v = JSON.parse(s);
    return v ?? fallback;
  } catch {
    return fallback;
  }
}

function useDebouncedValue(value, delay = 250) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debounced;
}

/* ===================== stable row key helpers ===================== */

function normalizeTsForKey(v) {
  if (!v) return "";
  const s = String(v);
  const d = new Date(s);
  if (!isNaN(d.getTime())) return String(d.getTime());
  return s.trim();
}

function makeLineItemKey(costSheetId, item) {
  if (item?.["Line Item ID"]) return String(item["Line Item ID"]);
  const csid = String(costSheetId || item?.["Cost Sheet ID"] || "").trim();
  const ts = normalizeTsForKey(
    item?.["Entry Timestamp"] || item?.["EntryTimestamp"] || item?.["Timestamp"]
  );
  const part = String(item?.["Particular"] || "").trim();
  return [csid || "-", ts || "-", part || "-"].join("|");
}

function normalizeSearchText(v) {
  return String(v ?? "").replace(/\s+/g, " ").trim().toLowerCase();
}

function normalizeLooseSearchText(v) {
  return normalizeSearchText(v).replace(/[^a-z0-9]/g, "");
}

function canonicalColumnName(v) {
  return normalizeLooseSearchText(v);
}

function getLineSearchValue(row, column) {
  if (!row || !column) return "";
  if (Object.prototype.hasOwnProperty.call(row, column)) return row[column] ?? "";

  const canonical = canonicalColumnName(column);
  if (row.__fieldText && Object.prototype.hasOwnProperty.call(row.__fieldText, canonical)) {
    return row.__fieldRaw?.[canonical] ?? "";
  }

  const foundKey = Object.keys(row).find((k) => !k.startsWith("__") && canonicalColumnName(k) === canonical);
  return foundKey ? row[foundKey] ?? "" : "";
}

function withLineSearchIndex(row) {
  const fieldText = {};
  const fieldLooseText = {};
  const fieldRaw = {};

  Object.keys(row || {}).forEach((key) => {
    if (key.startsWith("__")) return;

    const cleanKey = String(key || "").trim();
    const canonical = canonicalColumnName(cleanKey);
    if (!canonical) return;

    const value = row[key] ?? "";
    fieldRaw[canonical] = value;
    fieldText[canonical] = normalizeSearchText(value);
    fieldLooseText[canonical] = normalizeLooseSearchText(value);

    if (cleanKey && cleanKey !== key && !(cleanKey in row)) {
      row[cleanKey] = value;
    }
  });

  return {
    ...row,
    __fieldRaw: fieldRaw,
    __fieldText: fieldText,
    __fieldLooseText: fieldLooseText,
    __allText: Object.values(fieldText).join(" "),
    __allLooseText: Object.values(fieldLooseText).join(" "),
  };
}

function withCostSheetSearchIndex(row) {
  return {
    ...row,
    __searchText: normalizeSearchText(Object.values(row || {}).join(" ")),
  };
}

function csvEscapeCell(v) {
  const s = String(v ?? "");
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function csvCell(column, value) {
  return csvEscapeCell(formatDateCell(column, value));
}

function isActiveLineItem(row) {
  const active = String(row?.["Active"] ?? "Yes").trim().toLowerCase();
  return active !== "no";
}

function prepareActiveLineItems(costSheetId, items) {
  const activeOnly = (Array.isArray(items) ? items : []).filter(isActiveLineItem);

  return activeOnly.map((x, idx) => ({
    ...x,
    __lineKey: makeLineItemKey(costSheetId, x) || `${costSheetId}__fallback__${idx}`,
  }));
}

const LineItemSearchBox = React.memo(function LineItemSearchBox({
  initialValue,
  loading,
  disabled,
  onSubmit,
}) {
  const [draft, setDraft] = useState(initialValue || "");

  useEffect(() => {
    setDraft(initialValue || "");
  }, [initialValue]);

  const submit = () => {
    if (loading || disabled) return;
    onSubmit(draft);
  };

  return (
    <>
      <TextField
        size="small"
        label="Search line items"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            submit();
          }
        }}
        sx={{
          minWidth: { xs: "100%", sm: 220 },
          maxWidth: { sm: 260 },
          "& .MuiInputBase-input": { fontSize: 12 },
          "& .MuiInputLabel-root": { fontSize: 12 },
        }}
      />

      <Button
        variant="outlined"
        size="small"
        startIcon={<RefreshIcon />}
        onClick={submit}
        disabled={loading || disabled}
        sx={{ fontFamily: "Montserrat, sans-serif", fontSize: 11, minHeight: 38 }}
      >
        {loading ? "Searching..." : "Search"}
      </Button>
    </>
  );
});

/* ===================== Component ===================== */

export default function CostingTable() {
  const { user } = useAuth();
  const loggedInName = user?.username || user?.name || user?.email || "";
  const role = user?.role || "";
  const isAdmin = String(role || "").toLowerCase() === "admin";

  const [loading, setLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [downloadingCostSheetId, setDownloadingCostSheetId] = useState("");
  const [submitProgress, setSubmitProgress] = useState(null); // { done, total, label }

  const [validation, setValidation] = useState({
    heads: [],
    subcategories: {},
    paymentStatus: DEFAULT_PAYMENT_STATUSES,
  });

  const [costSheets, setCostSheets] = useState([]);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 250);

  const [statusFilter, setStatusFilter] = useState("");
  const [entityTypeFilter, setEntityTypeFilter] = useState("");

  const [columnsOpen, setColumnsOpen] = useState(null);
  const [visibleCols, setVisibleCols] = useState({});

  // Modal states
  const [openCreate, setOpenCreate] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);

  const [activeSheet, setActiveSheet] = useState(null);
  const [lineItems, setLineItems] = useState([]);
  const [lineItemHeaders, setLineItemHeaders] = useState([]);
  const [lineItemsRequested, setLineItemsRequested] = useState(false);
  const [lineItemsTotalCount, setLineItemsTotalCount] = useState(0);
  const [lineItemsOffset, setLineItemsOffset] = useState(0);

  const [mainTab, setMainTab] = useState("sheets");
  const [lineSearchRows, setLineSearchRows] = useState([]);
  const [lineSearchTotalCount, setLineSearchTotalCount] = useState(0);
  const [lineSearchServerTotals, setLineSearchServerTotals] = useState({
    qty: 0,
    amount: 0,
    gst: 0,
    total: 0,
  });
  const [lineSearchServerGroupedTotals, setLineSearchServerGroupedTotals] = useState([]);
  const [lineSearchLoading, setLineSearchLoading] = useState(false);
  const [lineSearchLoaded, setLineSearchLoaded] = useState(false);
  const [lineSearch, setLineSearch] = useState("");
  const [lineSearchSubmitted, setLineSearchSubmitted] = useState(false);
  const [lineSearchColumn, setLineSearchColumn] = useState("all");
  const [lineSearchMatchMode, setLineSearchMatchMode] = useState("contains");
  const [lineSearchEntityType, setLineSearchEntityType] = useState("");
  const [lineSearchPaymentStatus, setLineSearchPaymentStatus] = useState("");
  const [lineSearchHead, setLineSearchHead] = useState("");
  const [lineSearchGroupBy, setLineSearchGroupBy] = useState("Linked Entity Name");
  const [lineSearchColumnsAnchor, setLineSearchColumnsAnchor] = useState(null);
  const [lineSearchVisibleCols, setLineSearchVisibleCols] = useState({});

  // ✅ Entity dropdown options for CREATE modal
  const [entityOptions, setEntityOptions] = useState([]);
  const [entityLoading, setEntityLoading] = useState(false);

  // ✅ Drawer menu containment ref (FIX for dropdown hidden behind drawer)
  const drawerPaperRef = useRef(null);

  // ✅ MenuProps to force Select menus to render INSIDE the Drawer
  const getDrawerMenuProps = () => ({
    disablePortal: true,
    container: drawerPaperRef.current,
    PaperProps: {
      sx: {
        zIndex: (t) => (t?.zIndex?.modal ?? 1300) + 80,
      },
    },
  });

  // ===== Extraction =====
  const [openExtract, setOpenExtract] = useState(false);
  const [extractForm, setExtractForm] = useState({
    exportType: "costing", // "costing" | "finance"
    subtotalBy: "none", // ✅ finance only: "none" or a column name selected by user
    costSheetId: "",
    entityType: "",
    linkedEntityId: "",
    particular: "",
    paymentStatus: "",
    from: "",
    to: "",
    format: "csv",
    mergeBy: "none",
  });

  const [extractColumnsAnchor, setExtractColumnsAnchor] = useState(null);
  const [extractVisibleCols, setExtractVisibleCols] = useState({});
  const [extractColsTouched, setExtractColsTouched] = useState(false);

  const fallbackLineItemHeaders = useMemo(() => {
    return [
      "Cost Sheet ID",
      "Head Name",
      "Subcategory",
      "Expense Date",
      "Entry Timestamp",
      "Entered By",
      "Entry Tag",
      "Particular",
      "Details",
      "QTY",
      "Rate",
      "Amount",
      "GST %",
      "GST Amount",
      "Total Amount",
      "Attachment Link",
      "Voucher/Invoice No",
      "Payment Status",
      "Active",
      "Owner",
      "Linked Entity Type",
      "Linked Entity ID",
      "Linked Entity Name",
      "Client Name",
      "Project Type",
    ];
  }, []);

  const extractAllColumns = useMemo(() => {
    const hs = Array.isArray(lineItemHeaders) && lineItemHeaders.length ? lineItemHeaders : null;
    return hs || fallbackLineItemHeaders;
  }, [lineItemHeaders, fallbackLineItemHeaders]);

  const lineSearchDefaultCols = useMemo(() => {
    return [
      "Cost Sheet ID",
      "Linked Entity Type",
      "Linked Entity Name",
      "Client Name",
      "Project Type",
      "Owner",
      "Head Name",
      "Subcategory",
      "Expense Date",
      "Particular",
      "Details",
      "QTY",
      "Rate",
      "Amount",
      "GST Amount",
      "Total Amount",
      "Payment Status",
      "Voucher/Invoice No",
      "Entered By",
    ];
  }, []);

  const lineSearchAllColumns = useMemo(() => {
    const merged = new Set([...lineSearchDefaultCols, ...fallbackLineItemHeaders]);
    (lineSearchRows || []).forEach((row) => {
      Object.keys(row || {}).forEach((k) => {
        if (!k.startsWith("__")) merged.add(k);
      });
    });
    return Array.from(merged);
  }, [lineSearchRows, lineSearchDefaultCols, fallbackLineItemHeaders]);

  const lineSearchDisplayCols = useMemo(() => {
    return lineSearchAllColumns.filter((c) => lineSearchVisibleCols[c] !== false);
  }, [lineSearchAllColumns, lineSearchVisibleCols]);

  const lineSearchGroupOptions = useMemo(() => {
    return [
      "Linked Entity Name",
      "Linked Entity Type",
      "Client Name",
      "Project Type",
      "Owner",
      "Cost Sheet ID",
      "Head Name",
      "Subcategory",
      "Payment Status",
      "Particular",
      "Entered By",
    ].filter((c) => lineSearchAllColumns.includes(c));
  }, [lineSearchAllColumns]);

  // ✅ Build searchable "Cost Sheet options" (for Autocomplete dropdowns)
  const costSheetOptions = useMemo(() => {
    const arr = Array.isArray(costSheets) ? costSheets : [];
    return arr
      .map((cs) => {
        const id = String(cs?.["Cost Sheet ID"] || "").trim();
        const label = [cs?.["Cost Sheet ID"], cs?.["Linked Entity Name"], cs?.["Owner"], cs?.["Status"]]
          .filter(Boolean)
          .join(" — ");
        return {
          id,
          label: label || id,
          row: cs,
        };
      })
      .filter((x) => x.id);
  }, [costSheets]);

  useEffect(() => {
    if (!lineSearchAllColumns.length) return;

    setLineSearchVisibleCols((prev) => {
      if (prev && Object.keys(prev).length) {
        const next = { ...prev };
        lineSearchAllColumns.forEach((c) => {
          if (!(c in next)) next[c] = lineSearchDefaultCols.includes(c);
        });
        return next;
      }

      const initial = {};
      lineSearchAllColumns.forEach((c) => {
        initial[c] = lineSearchDefaultCols.includes(c);
      });
      return initial;
    });
  }, [lineSearchAllColumns, lineSearchDefaultCols]);

  useEffect(() => {
    if (lineSearchGroupOptions.length && !lineSearchGroupOptions.includes(lineSearchGroupBy)) {
      setLineSearchGroupBy(lineSearchGroupOptions[0]);
    }
  }, [lineSearchGroupBy, lineSearchGroupOptions]);

  const selectedExtractCostSheetOption = useMemo(() => {
    const id = String(extractForm?.costSheetId || "").trim();
    if (!id) return null;
    return costSheetOptions.find((x) => x.id === id) || null;
  }, [extractForm?.costSheetId, costSheetOptions]);

  useEffect(() => {
    if (!openExtract) return;

    const userKey = String(loggedInName || "").trim();
    const storageKey = getExtractColsStorageKey(userKey);

    const allDefault = {};
    (extractAllColumns || []).forEach((c) => (allDefault[c] = true));

    const cached = safeJsonParse(localStorage.getItem(storageKey), null);

    setExtractVisibleCols((prev) => {
      if (extractColsTouched && prev && Object.keys(prev).length) return prev;

      if (cached && typeof cached === "object") {
        const next = { ...allDefault };
        Object.keys(next).forEach((k) => {
          if (k in cached) next[k] = cached[k] !== false;
        });
        return next;
      }

      return allDefault;
    });
  }, [openExtract, extractAllColumns, loggedInName, extractColsTouched]);

  function openExtractColumns(e) {
    setExtractColumnsAnchor(e.currentTarget);
  }
  function closeExtractColumns() {
    setExtractColumnsAnchor(null);
  }

  const selectedExtractFields = useMemo(() => {
    const keys = Object.keys(extractVisibleCols || {});
    if (!keys.length) return [];
    return keys.filter((k) => extractVisibleCols[k] !== false);
  }, [extractVisibleCols]);

  // ✅ Finance subtotal dropdown options MUST come from the user-selected export columns
  const financeSubtotalOptions = useMemo(() => {
    const cols = Array.isArray(selectedExtractFields) && selectedExtractFields.length
      ? selectedExtractFields
      : (extractAllColumns || []);
    const order = new Map((extractAllColumns || []).map((c, i) => [c, i]));
    return [...cols].sort((a, b) => (order.get(a) ?? 999999) - (order.get(b) ?? 999999));
  }, [selectedExtractFields, extractAllColumns]);

  // ✅ Keep subtotalBy sane whenever exportType/columns change
  useEffect(() => {
    if (!openExtract) return;

    if (extractForm.exportType !== "finance") {
      if (extractForm.subtotalBy !== "none") {
        setExtractForm((p) => ({ ...p, subtotalBy: "none" }));
      }
      return;
    }

    const current = String(extractForm.subtotalBy || "none");
    if (current === "none") return;

    const ok = financeSubtotalOptions.includes(current);
    if (!ok) {
      setExtractForm((p) => ({ ...p, subtotalBy: "none" }));
    }
  }, [openExtract, extractForm.exportType, extractForm.subtotalBy, financeSubtotalOptions]);

  async function triggerExtraction() {
    const isSheetMode = String(extractForm.costSheetId || "").trim();

    const exportAction =
      extractForm.exportType === "finance" ? "exportFinance" : "exportCosting";

    const baseParams = {
      action: exportAction,
      mergeBy: extractForm.mergeBy,
      activeOnly: "true",
      excludeInactive: "true",
      dateFormat: "DD/MM/YYYY",

      ...(extractForm.exportType === "finance"
        ? {
            subtotalBy: String(extractForm.subtotalBy || "none"),
          }
        : {}),

      ...(isSheetMode
        ? { costSheetId: extractForm.costSheetId }
        : Object.fromEntries(
            Object.entries({
              entityType: extractForm.entityType,
              linkedEntityId: extractForm.linkedEntityId,
              particular: extractForm.particular,
              paymentStatus: extractForm.paymentStatus,
              from: extractForm.from,
              to: extractForm.to,
              format: extractForm.format,
            }).filter(([_, v]) => String(v || "").trim())
          )),

      ...Object.fromEntries(
        Object.entries({
          particular: extractForm.particular,
          paymentStatus: extractForm.paymentStatus,
          format: extractForm.format,
        }).filter(([_, v]) => String(v || "").trim())
      ),
    };

    const allCount = (extractAllColumns || []).length;
    const selCount = selectedExtractFields.length;

    const params = new URLSearchParams(baseParams);

    if (selCount > 0 && selCount < allCount) {
      params.set("fields", JSON.stringify(selectedExtractFields));
    }

    const url = `${BACKEND}?${params.toString()}`;

    try {
      setLoading(true);
      const response = await fetch(url);
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || "Export failed");
      }
      const blob = await response.blob();
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `${extractForm.exportType || "costing"}_${new Date().toISOString().slice(0, 10)}.${extractForm.format || "csv"}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(downloadUrl), 1000);
      setOpenExtract(false);
    } catch (error) {
      console.error("COSTING_EXPORT_ERROR", error);
      alert(error.message || "Failed to generate export");
    } finally {
      setLoading(false);
    }
  }

  // Create cost sheet form
  const [createForm, setCreateForm] = useState({
    Owner: "",
    "Linked Entity Type": "",
    "Linked Entity ID": "",
    "Linked Entity Name": "",
    "Client Name": "",
    "Project Type": "",
    Status: "Draft",
    Notes: "",
  });

  const selectedEntityOption = useMemo(() => {
    const id = String(createForm?.["Linked Entity ID"] || "");
    if (!id) return null;
    return (entityOptions || []).find((x) => String(x.id) === id) || null;
  }, [createForm, entityOptions]);

  const entityOptionLabel = (opt) => {
    if (!opt) return "";
    return String(opt.display || opt.name || opt.label || opt.id || "");
  };

  /* ===================== ADD EXPENSE ===================== */
  const [openAddExpense, setOpenAddExpense] = useState(false);
  const [addExpenseMode, setAddExpenseMode] = useState("existing"); // "existing" | "new"
  const [selectedExistingSheetId, setSelectedExistingSheetId] = useState("");
  const [advanceOptions, setAdvanceOptions] = useState([]);
  const [advanceLoading, setAdvanceLoading] = useState(false);
  const [advanceForm, setAdvanceForm] = useState({
    "Advance Date": new Date().toISOString().slice(0, 10),
    "Advance Paid To": "",
    "Advance Amount": "",
    Mode: "",
    "Reference No": "",
    Remarks: "",
  });

  const selectedAddExpenseCostSheetOption = useMemo(() => {
    const id = String(selectedExistingSheetId || "").trim();
    if (!id) return null;
    return costSheetOptions.find((x) => x.id === id) || null;
  }, [selectedExistingSheetId, costSheetOptions]);

  const [addExpenseItems, setAddExpenseItems] = useState([]);

  const loadSeq = useRef(0);
  const costSheetDetailsCache = useRef({});

  async function loadAdvancesForCostSheet(costSheetId) {
    const id = String(costSheetId || "").trim();
    if (!id) {
      setAdvanceOptions([]);
      return [];
    }

    setAdvanceLoading(true);
    try {
      const params = new URLSearchParams({
        action: "getAdvancesForCostSheet",
        costSheetId: id,
      });
      const data = await jsonpGet(`${BACKEND}?${params.toString()}`);
      const rows = Array.isArray(data) ? data : [];
      setAdvanceOptions(rows);
      return rows;
    } catch (e) {
      console.error("LOAD_ADVANCES_ERROR", e);
      setAdvanceOptions([]);
      return [];
    } finally {
      setAdvanceLoading(false);
    }
  }

  async function recordAdvanceForSelectedSheet() {
    if (!selectedExistingSheetId) {
      alert("Please select an existing Cost Sheet first.");
      return;
    }
    if (safeNum(advanceForm["Advance Amount"]) <= 0) {
      alert("Please enter a valid advance amount.");
      return;
    }
    if (!String(advanceForm["Advance Paid To"] || "").trim()) {
      alert("Please enter who the advance was paid to.");
      return;
    }

    setAdvanceLoading(true);
    try {
      await apiPost({
        action: "recordAdvancePayment",
        data: {
          ...advanceForm,
          "Cost Sheet ID": selectedExistingSheetId,
          "Collected By": loggedInName || "",
        },
      });
      setAdvanceForm({
        "Advance Date": new Date().toISOString().slice(0, 10),
        "Advance Paid To": "",
        "Advance Amount": "",
        Mode: "",
        "Reference No": "",
        Remarks: "",
      });
      setTimeout(() => loadAdvancesForCostSheet(selectedExistingSheetId), 350);
      alert("Advance payment recorded.");
    } catch (e) {
      console.error("RECORD_ADVANCE_ERROR", e);
      alert("Failed to record advance payment.");
    } finally {
      setAdvanceLoading(false);
    }
  }

  function getCachedLineItemPage(costSheetId, offset = 0) {
    const id = String(costSheetId || "").trim();
    const pageOffset = safeNum(offset);
    const entry = costSheetDetailsCache.current[id];
    if (!entry) return null;

    const page = entry.pages?.[pageOffset];
    if (page) {
      return {
        rows: page.rows || [],
        headers: page.headers || entry.headers || fallbackLineItemHeaders,
        totalCount: entry.totalCount || page.rows?.length || 0,
        offset: pageOffset,
      };
    }

    if (pageOffset === 0 && entry.rows) {
      return {
        rows: entry.rows || [],
        headers: entry.headers || fallbackLineItemHeaders,
        totalCount: entry.totalCount || entry.rows?.length || 0,
        offset: 0,
      };
    }

    return null;
  }

  function setCachedLineItemPage(costSheetId, offset, rows, headers, totalCount) {
    const id = String(costSheetId || "").trim();
    if (!id) return;

    const pageOffset = safeNum(offset);
    const prev = costSheetDetailsCache.current[id] || {};
    const next = {
      ...prev,
      pages: {
        ...(prev.pages || {}),
        [pageOffset]: { rows, headers },
      },
      headers,
      totalCount,
    };

    if (pageOffset === 0) {
      next.rows = rows;
      next.offset = 0;
    }

    costSheetDetailsCache.current[id] = next;
  }

  /**
   * Refresh committed costing data
   * The native API updates totals with each line-item mutation,
   * so the UI should NOT JSONP fan-out per cost sheet anymore.
   */
  async function refreshFast(options = {}) {
    const { refreshValidation = false } = options;
    const seq = ++loadSeq.current;
    setLoading(true);

    try {
      const needsValidation =
        refreshValidation ||
        !(validation.heads?.length ||
          validation.paymentStatus?.length ||
          Object.keys(validation.subcategories || {}).length);

      let parsedValidation = validation;

      if (needsValidation) {
        const rawV = await jsonpGet(`${BACKEND}?action=getValidation`);
        if (seq !== loadSeq.current) return;
        parsedValidation = normalizeValidationResponse(rawV);
        setValidation(parsedValidation);
      }

      const params = new URLSearchParams({
        action: "getCostSheets",
        fields: JSON.stringify(COST_SHEET_LIST_FIELDS),
      });

      const sheets = await jsonpGet(`${BACKEND}?${params.toString()}`);
      if (seq !== loadSeq.current) return;

      const arr = Array.isArray(sheets) ? sheets.map(withCostSheetSearchIndex) : [];
      setCostSheets(arr);

      if (arr.length) {
        const keys = Object.keys(arr[0]);
        const initial = {};
        keys.forEach((k) => (initial[k] = true));
        setVisibleCols((prev) => (prev && Object.keys(prev).length ? prev : initial));
      }

      if (activeSheet?.["Cost Sheet ID"]) {
        const id = String(activeSheet["Cost Sheet ID"]);
        const fresh = arr.find((x) => String(x?.["Cost Sheet ID"]) === id) || null;
        if (fresh) setActiveSheet(fresh);
      }
    } catch (e) {
      console.error("COSTING_REFRESH_FAST_ERROR", e);
      alert("Failed to load costing data. Check the server API and Google Sheets permissions.");
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }

  // Refresh the committed Sheets values after a mutation.
  async function refreshAfterMutation({ refreshValidation = false } = {}) {
    await refreshFast({ refreshValidation });
    setLineSearchLoaded(false);
  }

  async function loadLineItemSearchData(searchValue = lineSearch) {
    setLineSearchLoading(true);
    try {
      const params = new URLSearchParams({
        action: "searchCostLineItems",
        q: searchValue,
        searchColumn: lineSearchColumn,
        matchMode: lineSearchMatchMode,
        groupBy: lineSearchGroupBy,
        limit: "500",
        offset: "0",
      });

      if (lineSearchEntityType) params.set("entityType", lineSearchEntityType);
      if (lineSearchPaymentStatus) params.set("paymentStatus", lineSearchPaymentStatus);
      if (lineSearchHead) params.set("head", lineSearchHead);

      const data = await jsonpGet(`${BACKEND}?${params.toString()}`);
      if (!data?.success) {
        console.error("LINE_SEARCH_BACKEND_ERROR", data);
        throw new Error(data?.error || "Line item search failed.");
      }

      setLineSearchRows(Array.isArray(data.rows) ? data.rows.map(withLineSearchIndex) : []);
      setLineSearchTotalCount(safeNum(data.totalCount));
      setLineSearchServerTotals({
        qty: safeNum(data?.totals?.qty),
        amount: safeNum(data?.totals?.amount),
        gst: safeNum(data?.totals?.gst),
        total: safeNum(data?.totals?.totalAmount ?? data?.totals?.total),
      });
      setLineSearchServerGroupedTotals(Array.isArray(data.groupedTotals) ? data.groupedTotals : []);
      setLineSearchLoaded(true);
    } catch (e) {
      console.error("LINE_SEARCH_LOAD_ERROR", e);
      alert("Failed to load line item search data.");
    } finally {
      setLineSearchLoading(false);
    }
  }

  async function runLineItemSearch(searchValue = lineSearch) {
    const nextSearch = String(searchValue ?? "");
    const hasSearchValue = nextSearch.trim();
    const hasFilters =
      lineSearchEntityType || lineSearchPaymentStatus || lineSearchHead || lineSearchColumn !== "all";

    if (!hasSearchValue && !hasFilters) {
      alert("Please enter a search value or select a filter before searching.");
      return;
    }

    setLineSearch(nextSearch);
    setLineSearchSubmitted(true);
    await loadLineItemSearchData(nextSearch);
  }

  // On first mount, run FAST refresh
  useEffect(() => {
    refreshFast({ refreshValidation: true });
  }, []);

  async function ensureValidationLoaded() {
    const hasAny =
      (validation.heads && validation.heads.length) ||
      (validation.paymentStatus && validation.paymentStatus.length) ||
      (validation.subcategories && Object.keys(validation.subcategories).length);

    if (hasAny) return;

    try {
      const rawV = await jsonpGet(`${BACKEND}?action=getValidation`);
      const parsed = normalizeValidationResponse(rawV);
      setValidation(parsed);
    } catch (e) {
      console.error("ENSURE_VALIDATION_ERROR", e);
    }
  }

  const filtered = useMemo(() => {
    const q = normalizeSearchText(debouncedSearch);

    return costSheets
      .filter((r) => {
        const statusOk = !statusFilter || String(r["Status"] || "").trim() === statusFilter;
        const entityOk =
          !entityTypeFilter || String(r["Linked Entity Type"] || "").trim() === entityTypeFilter;

        const qOk = !q || String(r.__searchText || "").includes(q);
        return statusOk && entityOk && qOk;
      })
      .sort((a, b) => getCostSheetUpdatedMs(b) - getCostSheetUpdatedMs(a));
  }, [costSheets, debouncedSearch, statusFilter, entityTypeFilter]);

  const visibleCostSheets = useMemo(() => {
    return filtered.slice(0, COST_SHEET_RENDER_LIMIT);
  }, [filtered]);

  const costSheetColumns = useMemo(() => {
    if (!costSheets.length) return COST_SHEET_LIST_FIELDS;

    const merged = new Set(COST_SHEET_LIST_FIELDS);
    costSheets.forEach((row) => {
      Object.keys(row || {}).forEach((k) => merged.add(k));
    });
    return Array.from(merged);
  }, [costSheets]);

  const visibleCostSheetColumns = useMemo(() => {
    return costSheetColumns.filter((c) =>
      !String(c).startsWith("__") &&
      (visibleCols[c] === true || (COST_SHEET_OVERVIEW_FIELDS.includes(c) && visibleCols[c] !== false))
    );
  }, [costSheetColumns, visibleCols]);

  function openColumns(e) {
    setColumnsOpen(e.currentTarget);
  }
  function closeColumns() {
    setColumnsOpen(null);
  }

  function openLineSearchColumns(e) {
    setLineSearchColumnsAnchor(e.currentTarget);
  }
  function closeLineSearchColumns() {
    setLineSearchColumnsAnchor(null);
  }

  function openCreateModal() {
    setEntityOptions([]);
    setCreateForm((p) => ({
      ...p,
      Owner: isAdmin ? (p.Owner || loggedInName || "") : loggedInName || p.Owner,
      Status: "Draft",
      "Linked Entity Type": "",
      "Linked Entity ID": "",
      "Linked Entity Name": "",
    }));
    setOpenCreate(true);
  }

  async function loadEntitiesForType(type) {
    const t = String(type || "").trim();
    if (!t) {
      setEntityOptions([]);
      return;
    }

    setEntityLoading(true);
    try {
      const res = await jsonpGet(
        `${BACKEND}?action=getEntities&type=${encodeURIComponent(t)}&owner=${encodeURIComponent(
          loggedInName || ""
        )}&role=${encodeURIComponent(role || "")}`
      );

      if (res?.success && Array.isArray(res.entities)) {
        setEntityOptions(res.entities);
      } else {
        console.error("GET_ENTITIES_ERROR", res);
        setEntityOptions([]);
        alert(
          "Entities could not be loaded. Check the source sheet header mapping."
        );
      }
    } catch (e) {
      console.error("GET_ENTITIES_FETCH_ERROR", e);
      setEntityOptions([]);
      alert("Entities could not be loaded. Check server access and sheet permissions.");
    } finally {
      setEntityLoading(false);
    }
  }

  async function createCostSheet() {
    setLoading(true);
    try {
      const payload = {
        ...createForm,
        Owner: isAdmin ? (createForm.Owner || loggedInName || "") : loggedInName || createForm.Owner,
      };

      await apiPost({ action: "createCostSheet", data: payload });
      setOpenCreate(false);

      // ✅ robust refresh
      setTimeout(async () => {
        await refreshAfterMutation();
      }, 250);

      alert("Cost Sheet created. Refreshing list…");
    } catch (e) {
      console.error("CREATE_COST_SHEET_ERROR", e);
      alert("Failed to create cost sheet.");
    } finally {
      setLoading(false);
    }
  }

  async function openEditModal(row) {
    const id = String(row?.["Cost Sheet ID"] || "").trim();
    const cached = id ? getCachedLineItemPage(id, 0) : null;

    setActiveSheet(row);
    setOpenEdit(true);
    setLineItems(cached?.rows || []);
    setLineItemHeaders(cached?.headers || fallbackLineItemHeaders);
    setLineItemsTotalCount(cached?.totalCount || cached?.rows?.length || 0);
    setLineItemsOffset(cached?.offset || 0);
    setLineItemsRequested(Boolean(cached));
    setDetailsLoading(false);
    ensureValidationLoaded();
  }

  async function fetchLineItemsForModal(costSheetId, { offset = 0, limit = LINE_ITEM_RENDER_LIMIT } = {}) {
    const params = new URLSearchParams({
      action: "searchCostLineItems",
      q: costSheetId,
      searchColumn: "Cost Sheet ID",
      matchMode: "exact",
      groupBy: "Cost Sheet ID",
      limit: String(limit),
      offset: String(offset),
    });

    const data = await jsonpGet(`${BACKEND}?${params.toString()}`);
    if (data?.success && Array.isArray(data.rows)) {
      const rows = prepareActiveLineItems(costSheetId, data.rows);
      return {
        rows,
        totalCount: safeNum(data.totalCount) || rows.length,
      };
    }

    console.warn("LINE_ITEMS_SEARCH_FALLBACK_TO_DETAILS", data);
    const items = await jsonpGet(
      `${BACKEND}?action=getCostSheetDetails&costSheetId=${encodeURIComponent(costSheetId)}`
    );

    if (!Array.isArray(items)) {
      console.error("DETAILS_NOT_ARRAY", items);
      throw new Error("This cost sheet returned invalid detail data.");
    }

    const allRows = prepareActiveLineItems(costSheetId, items);
    const rows = allRows.slice(offset, offset + limit);
    return {
      rows,
      totalCount: allRows.length,
    };
  }

  async function loadLineItemsForSheet(costSheetId, { force = false, offset = 0 } = {}) {
    const id = String(costSheetId || "").trim();
    if (!id) return;

    const cached = getCachedLineItemPage(id, offset);
    if (cached && !force) {
      setLineItems(cached.rows || []);
      setLineItemHeaders(cached.headers || fallbackLineItemHeaders);
      setLineItemsTotalCount(cached.totalCount || cached.rows?.length || 0);
      setLineItemsOffset(cached.offset || 0);
      setLineItemsRequested(true);
      return;
    }

    setLineItemsRequested(true);
    setDetailsLoading(true);
    try {
      const { rows: withKeys, totalCount } = await fetchLineItemsForModal(id, { offset });

      const keys = withKeys.map((x) => x.__lineKey);
      const dupes = keys.filter((k, i) => keys.indexOf(k) !== i);
      if (dupes.length) {
        console.warn("DUPLICATE_LINE_KEYS", dupes);
      }

      setLineItems(withKeys);
      setLineItemsTotalCount(totalCount);
      setLineItemsOffset(offset);

      const mergedHeaders = Array.from(
        new Set(
          withKeys.flatMap((x) => Object.keys(x || {})).filter((h) => h !== "__lineKey")
        )
      );

      const headers = mergedHeaders.length ? mergedHeaders : fallbackLineItemHeaders;
      setLineItemHeaders(headers);
      setCachedLineItemPage(id, offset, withKeys, headers, totalCount);
    } catch (e) {
      console.error("OPEN_EDIT_COST_SHEET_ERROR", e);
      alert(e?.message || "Failed to open cost sheet details.");
      setLineItems([]);
      setLineItemHeaders(fallbackLineItemHeaders);
      setLineItemsTotalCount(0);
    } finally {
      setDetailsLoading(false);
    }
  }

  async function openEditModalById(costSheetId) {
    const list = costSheets || [];

    const row =
      list.find(
        (x) =>
          String(x?.["Cost Sheet ID"] || "").trim() ===
          String(costSheetId || "").trim()
      ) || null;

    if (!row) {
      console.error("OPEN_EDIT_FAIL", {
        requestedId: costSheetId,
        availableIds: list.map((x) => x?.["Cost Sheet ID"]),
      });
      alert("Could not find the selected Cost Sheet. Please refresh and try again.");
      return;
    }

    await openEditModal(row);
  }

  async function openLineItemFromSearch(row) {
    const costSheetId = String(getLineSearchValue(row, "Cost Sheet ID") || "").trim();
    if (!costSheetId) {
      alert("Cannot edit this line item because Cost Sheet ID is missing.");
      return;
    }

    const sheetRow =
      (costSheets || []).find(
        (x) => String(x?.["Cost Sheet ID"] || "").trim() === costSheetId
      ) || {
        "Cost Sheet ID": costSheetId,
        "Linked Entity Type": getLineSearchValue(row, "Linked Entity Type"),
        "Linked Entity ID": getLineSearchValue(row, "Linked Entity ID"),
        "Linked Entity Name": getLineSearchValue(row, "Linked Entity Name"),
        "Client Name": getLineSearchValue(row, "Client Name"),
        "Project Type": getLineSearchValue(row, "Project Type"),
        Owner: getLineSearchValue(row, "Owner"),
      };

    const prepared = prepareActiveLineItems(costSheetId, [row]);
    if (!prepared.length) {
      alert("This line item is inactive and cannot be edited from the fast editor.");
      return;
    }

    const headers = Array.from(
      new Set(
        [...fallbackLineItemHeaders, ...Object.keys(prepared[0] || {})].filter(
          (h) => h && !String(h).startsWith("__")
        )
      )
    );

    setActiveSheet(sheetRow);
    setOpenEdit(true);
    setLineItems(prepared);
    setLineItemHeaders(headers);
    setLineItemsTotalCount(1);
    setLineItemsOffset(0);
    setLineItemsRequested(true);
    setDetailsLoading(false);
    await openDrawerEdit(prepared[0]);
  }

  async function refreshLineItemsForActiveSheet(costSheetId) {
    await loadLineItemsForSheet(costSheetId, { force: true, offset: lineItemsOffset });
  }

  /**
   * Adds and audit-preserving edits commit through the native Sheets API.
   */
  async function addLineItemRow(row, original = null) {
    if (!activeSheet) return;
    const costSheetId = activeSheet["Cost Sheet ID"];

    let payloadRow = computeRowTotals({ ...(row || {}) });

    // strip UI-only flags before POST
    const uiKeys = ["__hasQtyRate", "__useAutoAmount", "__useManualTotal", "__lineKey"];
    uiKeys.forEach((k) => {
      if (k in payloadRow) delete payloadRow[k];
    });

    payloadRow["Cost Sheet ID"] = costSheetId;

    payloadRow["Owner"] = activeSheet?.["Owner"] || payloadRow["Owner"] || loggedInName || "";
    payloadRow["Linked Entity Type"] =
      activeSheet?.["Linked Entity Type"] || payloadRow["Linked Entity Type"] || "";
    payloadRow["Linked Entity ID"] =
      activeSheet?.["Linked Entity ID"] || payloadRow["Linked Entity ID"] || "";
    payloadRow["Linked Entity Name"] =
      activeSheet?.["Linked Entity Name"] || payloadRow["Linked Entity Name"] || "";

    payloadRow["Client Name"] = activeSheet?.["Client Name"] || payloadRow["Client Name"] || "";
    payloadRow["Project Type"] = activeSheet?.["Project Type"] || payloadRow["Project Type"] || "";

    payloadRow["Entered By"] = loggedInName || payloadRow["Entered By"] || "";


    const hasSome =
      String(payloadRow.Particular || "").trim() ||
      String(payloadRow.Details || "").trim() ||
      String(payloadRow.Amount || "").trim() ||
      String(payloadRow["Total Amount"] || "").trim();

    if (!hasSome) {
      alert("Please enter at least Particular / Details / Amount / Total Amount.");
      return;
    }

    setLoading(true);
    try {
      await apiPost({
        action: original ? "updateLineItem" : "addLineItemsBatch",
        data: original ? {
          costSheetId,
          lineItemId: original["Line Item ID"],
          particular: original.Particular,
          entryTimestamp: original["Entry Timestamp"],
          updated: payloadRow,
        } : { costSheetId, items: [payloadRow] },
      });

      delete costSheetDetailsCache.current[costSheetId];
      setTimeout(async () => {
        const tasks = [refreshAfterMutation()];
        if (lineItemsRequested) tasks.unshift(refreshLineItemsForActiveSheet(costSheetId));
        await Promise.all(tasks).catch((error) => console.error("COSTING_REFRESH_ERROR", error));
      }, 0);
      return true;
    } catch (e) {
      console.error("ADD_LINE_ITEM_ERROR", e);
      alert(e.message || "Failed to save line item.");
      return false;
    } finally {
      setLoading(false);
    }
  }

  /**
   * ✅ UPDATED DELETE (non-breaking)
   * - stable key for optimistic UI
   * - sends both particular + entryTimestamp (backend may ignore extra fields safely)
   */
  async function softDeleteLineItem(item) {
    if (!activeSheet) return;
    const costSheetId = activeSheet["Cost Sheet ID"];

    const particular = String(item?.["Particular"] || "").trim();
    const entryTsRaw = item?.["Entry Timestamp"];
    const entryTimestamp = String(entryTsRaw ?? "").trim();
    const lineKey = item?.__lineKey || makeLineItemKey(costSheetId, item);

    if (!particular && !entryTimestamp) {
      alert("Cannot delete: This row has no Particular and no Entry Timestamp.");
      return;
    }

    setLoading(true);
    try {
      await apiPost({
        action: "softDeleteLineItem",
        data: {
          costSheetId,
          lineItemId: item["Line Item ID"],
          particular,
          entryTimestamp,
          lineKey,
        },
      });

      setLineItems((p) => (p || []).filter((x) => (x?.__lineKey || "") !== lineKey));

      setTimeout(async () => {
        await Promise.all([
          refreshLineItemsForActiveSheet(costSheetId),
          refreshAfterMutation(),
        ]);
      }, 150);
    } catch (e) {
      console.error("SOFT_DELETE_ERROR", e);
      alert(e.message || "Failed to delete line item.");
    } finally {
      setLoading(false);
    }
  }

  const totalsByHead = useMemo(() => {
    const out = {};
    (validation.heads || []).forEach((h) => (out[h] = 0));
    (lineItems || []).forEach((li) => {
      const h = String(li["Head Name"] || "").trim();
      const total = safeNum(li["Total Amount"]);
      out[h] = safeNum(out[h]) + total;
    });
    return out;
  }, [lineItems, validation.heads]);

  const grandTotal = useMemo(() => {
    return Object.values(totalsByHead).reduce((a, b) => a + safeNum(b), 0);
  }, [totalsByHead]);

  const displayedGrandTotal = safeNum(activeSheet?.["Grand Total"]) || grandTotal;
  const displayedPageTotal = grandTotal;

  const subcatsForHead = (head) => {
    const m = validation.subcategories || {};
    return Array.isArray(m[head]) ? m[head] : [];
  };

  /* ===================== Drawer Editor State ===================== */

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState("add"); // "add" | "edit"
  const [drawerOriginal, setDrawerOriginal] = useState(null);

  const blankDraft = (headPreset) =>
    recomputeDraftLive({
      "Head Name": headPreset || "",
      Subcategory: "",
      "Expense Date": new Date().toISOString().slice(0, 10),
      "Entered By": loggedInName,
      "Entry Tag": "",
      Particular: "",
      Details: "",
      QTY: "",
      Rate: "",
      Amount: "",
      "GST %": "",
      "GST Amount": "",
      "Total Amount": "",
      "Attachment Link": "",
      "Voucher/Invoice No": "",
      "Payment Status": "",
      "Payment Source": "Company Paid",
      "Advance ID": "",
      "Advance Applied Amount": "",
      __useAutoAmount: true,
      __useManualTotal: false,
    });

  const [drawerDraft, setDrawerDraft] = useState(() => blankDraft(""));

  useEffect(() => {
    setDrawerDraft((p) =>
      recomputeDraftLive({
        ...p,
        "Entered By": loggedInName || p["Entered By"] || "",
      })
    );
  }, [loggedInName]);

  async function openDrawerAdd(headPreset) {
    await ensureValidationLoaded();

    setDrawerMode("add");
    setDrawerOriginal(null);
    setDrawerDraft(blankDraft(headPreset));
    setDrawerOpen(true);
  }

  async function openDrawerEdit(item) {
    await ensureValidationLoaded();

    const headName = String(item?.["Head Name"] || "").trim();
    setDrawerMode("edit");
    setDrawerOriginal(item || null);

    const pre = recomputeDraftLive({
      "Head Name": headName,
      Subcategory: item?.Subcategory ?? "",
      "Expense Date": item?.["Expense Date"] ?? new Date().toISOString().slice(0, 10),
      "Entered By": loggedInName,
      "Entry Tag": item?.["Entry Tag"] ?? "",
      Particular: item?.Particular ?? "",
      Details: item?.Details ?? "",
      QTY: item?.["QTY"] ?? "",
      Rate: item?.["Rate"] ?? "",
      Amount: item?.["Amount"] ?? "",
      "GST %": item?.["GST %"] ?? "",
      "GST Amount": item?.["GST Amount"] ?? "",
      "Total Amount": item?.["Total Amount"] ?? "",
      "Attachment Link": item?.["Attachment Link"] ?? "",
      "Voucher/Invoice No": item?.["Voucher/Invoice No"] ?? "",
      "Payment Status": item?.["Payment Status"] ?? "",
      __useAutoAmount: true,
      __useManualTotal:
        !(safeNum(item?.["QTY"]) > 0 && safeNum(item?.["Rate"]) > 0) &&
        String(item?.["Total Amount"] ?? "").trim() !== "" &&
        String(item?.["Amount"] ?? "").trim() === "",
    });

    setDrawerDraft(pre);
    setDrawerOpen(true);
  }

  function closeDrawer() {
    setDrawerOpen(false);
  }

  function setDrawerField(key, value) {
    setDrawerDraft((p) => {
      const next = { ...p };

      if (key === "Head Name") {
        next["Head Name"] = value;
        next.Subcategory = "";
        return recomputeDraftLive(next);
      }

      if (key === "__useAutoAmount") {
        next.__useAutoAmount = Boolean(value);
        return recomputeDraftLive(next);
      }

      if (key === "Amount") {
        next.Amount = value;
        next.__useAutoAmount = false;
        next.__useManualTotal = false;
        return recomputeDraftLive(next);
      }

      if (key === "Total Amount") {
        next["Total Amount"] = value;
        next.__useManualTotal = true;
        next.__useAutoAmount = false;
        return recomputeDraftLive(next);
      }

      next[key] = value;

      if (key === "QTY" || key === "Rate") {
        if (next.__useAutoAmount !== false) next.__useAutoAmount = true;
        if (safeNum(next["QTY"]) > 0 && safeNum(next["Rate"]) > 0) {
          next.__useManualTotal = false;
        }
      }

      return recomputeDraftLive(next);
    });
  }

  /**
   * ✅ UPDATED EDIT SAVE
   * old row -> Active=No, new row -> append
   */
  async function saveDrawer() {
    if (!activeSheet || loading) return false;
    const saved = await addLineItemRow(drawerDraft, drawerMode === "edit" ? drawerOriginal : null);
    if (saved) setDrawerOpen(false);
    return Boolean(saved);
  }

  async function saveDrawerAndNew() {
    if (!activeSheet || loading) return;
    const headKeep = String(drawerDraft?.["Head Name"] || "").trim();
    const saved = await addLineItemRow(drawerDraft, drawerMode === "edit" ? drawerOriginal : null);
    if (saved) {
      setDrawerMode("add");
      setDrawerOriginal(null);
      setDrawerDraft(blankDraft(headKeep));
    }
  }

  /* ===================== Add Expense Modal helpers (MULTI ITEMS) ===================== */

  function setAddExpenseItemField(index, key, value) {
    setAddExpenseItems((prev) => {
      const next = [...prev];
      const cur = next[index] ? { ...next[index] } : blankDraft("");

      if (key === "__useAutoAmount") {
        const updated = { ...cur, __useAutoAmount: Boolean(value) };
        updated.__useManualTotal = false;
        next[index] = recomputeDraftLive(updated);
        return next;
      }

      if (key === "Amount") {
        const updated = { ...cur, Amount: value, __useAutoAmount: false, __useManualTotal: false };
        next[index] = recomputeDraftLive(updated);
        return next;
      }

      if (key === "Total Amount") {
        const updated = {
          ...cur,
          "Total Amount": value,
          __useManualTotal: true,
          __useAutoAmount: false,
        };
        next[index] = recomputeDraftLive(updated);
        return next;
      }

      const updated = { ...cur, [key]: value };
      if (key === "Head Name") updated.Subcategory = "";
      if (key === "Payment Source" && value !== "Advance") {
        updated["Advance ID"] = "";
        updated["Advance Applied Amount"] = "";
      }
      if (key === "Advance ID" && value && !updated["Advance Applied Amount"]) {
        updated["Advance Applied Amount"] = updated["Total Amount"] || "";
      }

      if (key === "QTY" || key === "Rate") {
        if (updated.__useAutoAmount !== false) updated.__useAutoAmount = true;
        if (safeNum(updated["QTY"]) > 0 && safeNum(updated["Rate"]) > 0) {
          updated.__useManualTotal = false;
        }
      }

      next[index] = recomputeDraftLive(updated);
      return next;
    });
  }

  function addAnotherExpenseItem() {
    setAddExpenseItems((prev) => {
      const last = prev && prev.length ? prev[prev.length - 1] : null;
      const headKeep = String(last?.["Head Name"] || "").trim();
      return [...(prev || []), blankDraft(headKeep)];
    });
  }

  function removeExpenseItem(index) {
    setAddExpenseItems((prev) => {
      const arr = [...(prev || [])];
      arr.splice(index, 1);
      return arr.length ? arr : [blankDraft("")];
    });
  }

  const addExpenseTotals = useMemo(() => {
    const items = Array.isArray(addExpenseItems) ? addExpenseItems : [];
    const sum = items.reduce((acc, it) => acc + safeNum(it?.["Total Amount"]), 0);
    return {
      count: items.length,
      total: sum,
    };
  }, [addExpenseItems]);

  function openAddExpenseModal() {
    setAddExpenseMode("existing");
    setSelectedExistingSheetId("");
    setAdvanceOptions([]);
    setAdvanceForm({
      "Advance Date": new Date().toISOString().slice(0, 10),
      "Advance Paid To": "",
      "Advance Amount": "",
      Mode: "",
      "Reference No": "",
      Remarks: "",
    });
    setEntityOptions([]);
    setCreateForm((p) => ({
      ...p,
      Owner: isAdmin ? (p.Owner || loggedInName || "") : loggedInName || p.Owner,
      Status: "Draft",
      "Linked Entity Type": "",
      "Linked Entity ID": "",
      "Linked Entity Name": "",
    }));
    setAddExpenseItems([blankDraft("")]);
    setOpenAddExpense(true);
  }

  /**
   * ✅ Efficiency update:
   * - Existing sheet: addLineItemsBatch (single POST)
   * - New sheet: createCostSheetAndAddLineItems (single POST)
   */
  async function submitAddExpense() {
    await ensureValidationLoaded();

    const items = Array.isArray(addExpenseItems) ? addExpenseItems : [];
    if (!items.length) {
      alert("Please add at least one line item.");
      return;
    }

    const cleanedItems = items.map((it) => {
      let payloadRow = computeRowTotals({ ...(it || {}) });

      const uiKeys = ["__hasQtyRate", "__useAutoAmount", "__useManualTotal", "__lineKey"];
      uiKeys.forEach((k) => {
        if (k in payloadRow) delete payloadRow[k];
      });

      payloadRow["Entered By"] = loggedInName || payloadRow["Entered By"] || "";
      return payloadRow;
    });

    const validItems = cleanedItems.filter((payloadRow) => {
      const hasSome =
        String(payloadRow.Particular || "").trim() ||
        String(payloadRow.Details || "").trim() ||
        String(payloadRow.Amount || "").trim() ||
        String(payloadRow["Total Amount"] || "").trim();
      return hasSome;
    });

    if (!validItems.length) {
      alert(
        "Please enter at least Particular / Details / Amount / Total Amount in at least one line item."
      );
      return;
    }

    const invalidAdvanceItem = validItems.find((item) => {
      if (String(item["Payment Source"] || "") !== "Advance") return false;
      return !String(item["Advance ID"] || "").trim() || safeNum(item["Advance Applied Amount"]) <= 0;
    });
    if (invalidAdvanceItem) {
      alert("Please select an advance and enter the amount used for every advance-funded line item.");
      return;
    }

    setLoading(true);
    setSubmitProgress({ done: 0, total: validItems.length, label: "Submitting line items…" });

    try {
      if (addExpenseMode === "existing") {
        if (!selectedExistingSheetId) {
          alert("Please select an existing Cost Sheet.");
          return;
        }

        const sheetRow = (costSheets || []).find(
          (x) => String(x["Cost Sheet ID"]) === String(selectedExistingSheetId)
        );

        const payloads = validItems.map((p) => ({
          ...p,
          "Cost Sheet ID": selectedExistingSheetId,
          Owner: sheetRow?.["Owner"] || p["Owner"] || loggedInName || "",
          "Linked Entity Type": sheetRow?.["Linked Entity Type"] || p["Linked Entity Type"] || "",
          "Linked Entity ID": sheetRow?.["Linked Entity ID"] || p["Linked Entity ID"] || "",
          "Linked Entity Name": sheetRow?.["Linked Entity Name"] || p["Linked Entity Name"] || "",
          "Client Name": sheetRow?.["Client Name"] || p["Client Name"] || "",
          "Project Type": sheetRow?.["Project Type"] || p["Project Type"] || "",
        }));

        await apiPost({
          action: "addLineItemsBatch",
          data: {
            costSheetId: selectedExistingSheetId,
            items: payloads,
          },
        });

        setSubmitProgress((prev) =>
          prev ? { ...prev, done: prev.total } : { done: payloads.length, total: payloads.length }
        );

        setOpenAddExpense(false);

        setTimeout(async () => {
          await refreshAfterMutation();
        }, 350);

        alert(
          payloads.length === 1
            ? "Line item added to existing Cost Sheet."
            : `${payloads.length} line items added to existing Cost Sheet.`
        );
        return;
      }

      // NEW COST SHEET MODE
      if (!createForm["Linked Entity Type"] || !createForm["Linked Entity ID"]) {
        alert("Please select Linked Entity Type and Linked Entity Name for the new Cost Sheet.");
        return;
      }

      const costSheetData = {
        ...createForm,
        Owner: isAdmin ? (createForm.Owner || loggedInName || "") : loggedInName || createForm.Owner,
      };

      await apiPost({
        action: "createCostSheetAndAddLineItems",
        data: {
          costSheetData,
          items: validItems.map((p) => ({
            ...p,
            Owner: costSheetData.Owner,
            "Linked Entity Type": costSheetData["Linked Entity Type"],
            "Linked Entity ID": costSheetData["Linked Entity ID"],
            "Linked Entity Name": costSheetData["Linked Entity Name"],
            "Client Name": costSheetData["Client Name"] || "",
            "Project Type": costSheetData["Project Type"] || "",
          })),
        },
      });

      setSubmitProgress((prev) =>
        prev ? { ...prev, done: prev.total } : { done: validItems.length, total: validItems.length }
      );

      setOpenAddExpense(false);

      setTimeout(async () => {
        await refreshAfterMutation();
      }, 350);

      alert("New Cost Sheet created and line items added.");
    } catch (e) {
      console.error("ADD_EXPENSE_ERROR", e);
      alert(e?.message || "Failed to submit expense.");
    } finally {
      setLoading(false);
      setSubmitProgress(null);
    }
  }

  /* ===================== Table columns for line items ===================== */

  const defaultLineItemCols = [
    "Head Name",
    "Subcategory",
    "Expense Date",
    "Particular",
    "QTY",
    "Rate",
    "Amount",
    "GST %",
    "GST Amount",
    "Total Amount",
    "Payment Status",
    "Attachment Link",
    "Voucher/Invoice No",
    "Entered By",
    "Entry Tag",
    "Details",
  ];

  const lineItemCols = useMemo(() => {
    if (!lineItemHeaders?.length) return defaultLineItemCols;

    const available = new Set(lineItemHeaders);
    const picked = defaultLineItemCols.filter((c) => available.has(c));

    if (!picked.length) return lineItemHeaders.filter((h) => h !== "Active");
    return picked;
  }, [lineItemHeaders]);

  const visibleLineItems = useMemo(() => {
    return (lineItems || []).slice(0, LINE_ITEM_RENDER_LIMIT);
  }, [lineItems]);

  const loadedLineItemCount = (lineItems || []).length;
  const lineItemsKnownTotal = Math.max(lineItemsTotalCount || 0, loadedLineItemCount);
  const lineItemsRangeStart = lineItemsRequested && loadedLineItemCount ? lineItemsOffset + 1 : 0;
  const lineItemsRangeEnd = lineItemsOffset + loadedLineItemCount;
  const hasPrevLineItemPage = lineItemsRequested && lineItemsOffset > 0;
  const hasNextLineItemPage = lineItemsRequested && lineItemsRangeEnd < lineItemsKnownTotal;
  const hiddenLineItemCount = Math.max(0, lineItemsKnownTotal - lineItemsRangeEnd);

  function loadLineItemPage(nextOffset) {
    const safeOffset = Math.max(0, safeNum(nextOffset));
    return loadLineItemsForSheet(activeSheet?.["Cost Sheet ID"], {
      force: false,
      offset: safeOffset,
    });
  }

  const filteredLineSearchRows = useMemo(() => {
    if (!lineSearchSubmitted) return [];
    return lineSearchRows || [];
  }, [lineSearchRows, lineSearchSubmitted]);

  const lineSearchTotals = useMemo(() => {
    return {
      count: lineSearchSubmitted ? lineSearchTotalCount : 0,
      qty: lineSearchServerTotals.qty,
      amount: lineSearchServerTotals.amount,
      gst: lineSearchServerTotals.gst,
      total: lineSearchServerTotals.total,
    };
  }, [lineSearchSubmitted, lineSearchTotalCount, lineSearchServerTotals]);

  const lineSearchGroupedTotals = useMemo(() => {
    return (lineSearchServerGroupedTotals || []).map((row) => ({
      ...row,
      total: safeNum(row.totalAmount ?? row.total),
    }));
  }, [lineSearchServerGroupedTotals]);

  const visibleLineSearchRows = useMemo(() => {
    return filteredLineSearchRows.slice(0, 500);
  }, [filteredLineSearchRows]);

  function downloadLineSearchCsv() {
    const rows = Array.isArray(filteredLineSearchRows) ? filteredLineSearchRows : [];
    if (!rows.length) {
      alert("No matching line items to download.");
      return;
    }

    const headers = lineSearchDisplayCols && lineSearchDisplayCols.length
      ? lineSearchDisplayCols
      : lineSearchAllColumns;

    const lines = [
      headers.map(csvEscapeCell).join(","),
      ...rows.map((row) =>
        headers.map((c) => csvCell(c, getLineSearchValue(row, c))).join(",")
      ),
    ];

    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `costing_line_items_${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      URL.revokeObjectURL(url);
      a.remove();
    }, 300);
  }

  async function downloadActiveCostSheetCsv(sheetRow) {
    const costSheetId = String(sheetRow?.["Cost Sheet ID"] || "").trim();
    if (!costSheetId) {
      alert("Cannot download: Cost Sheet ID is missing.");
      return;
    }

    setDownloadingCostSheetId(costSheetId);
    try {
      const items = await jsonpGet(
        `${BACKEND}?action=getCostSheetDetails&costSheetId=${encodeURIComponent(costSheetId)}`
      );

      if (!Array.isArray(items)) {
        console.error("DOWNLOAD_DETAILS_NOT_ARRAY", items);
        alert("Could not download this cost sheet. Detail data was invalid.");
        return;
      }

      const rows = prepareActiveLineItems(costSheetId, items);
      if (!rows.length) {
        alert("No active line items found for this cost sheet.");
        return;
      }

      const headers = Array.from(
        new Set(
          [...fallbackLineItemHeaders, ...rows.flatMap((row) => Object.keys(row || {}))]
            .filter((h) => h && !String(h).startsWith("__") && h !== "Active")
        )
      );

      const lines = [
        headers.map(csvEscapeCell).join(","),
        ...rows.map((row) => headers.map((h) => csvCell(h, row[h])).join(",")),
      ];

      const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `cost_sheet_${costSheetId}_${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        URL.revokeObjectURL(url);
        a.remove();
      }, 300);
    } catch (e) {
      console.error("DOWNLOAD_ACTIVE_COST_SHEET_ERROR", e);
      alert("Failed to download active cost sheet data.");
    } finally {
      setDownloadingCostSheetId("");
    }
  }

  /* ===================== Keyboard Shortcuts ===================== */

  function isEditableTarget(el) {
    if (!el) return false;
    const tag = String(el.tagName || "").toLowerCase();
    if (tag === "input" || tag === "textarea" || tag === "select") return true;
    if (el.isContentEditable) return true;
    return false;
  }

  useEffect(() => {
    const onKeyDown = (e) => {
      const key = String(e.key || "").toLowerCase();
      const ctrl = e.ctrlKey || e.metaKey;
      const shift = e.shiftKey;

      if (key === "escape") {
        if (drawerOpen) {
          e.preventDefault();
          closeDrawer();
          return;
        }
        if (openAddExpense) {
          e.preventDefault();
          setOpenAddExpense(false);
          return;
        }
        if (openCreate) {
          e.preventDefault();
          setOpenCreate(false);
          return;
        }
        if (openExtract) {
          e.preventDefault();
          setOpenExtract(false);
          return;
        }
        if (openEdit) {
          e.preventDefault();
          setOpenEdit(false);
          return;
        }
        return;
      }

      if (ctrl && key === "enter") {
        if (drawerOpen) {
          e.preventDefault();
          if (!loading) saveDrawer();
          return;
        }
        if (openAddExpense) {
          e.preventDefault();
          if (!loading) submitAddExpense();
          return;
        }
        if (openCreate) {
          e.preventDefault();
          if (!loading) createCostSheet();
          return;
        }
        return;
      }

      if (ctrl && shift && key === "enter") {
        if (drawerOpen) {
          e.preventDefault();
          if (!loading) saveDrawerAndNew();
          return;
        }
      }

      if (ctrl && shift && key === "a") {
        if (openAddExpense) {
          e.preventDefault();
          addAnotherExpenseItem();
          return;
        }
      }

      if (ctrl && shift && key === "e") {
        e.preventDefault();
        openAddExpenseModal();
        return;
      }
      if (ctrl && shift && key === "c") {
        e.preventDefault();
        openCreateModal();
        return;
      }

      if (isEditableTarget(e.target) && !ctrl) return;
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [
    drawerOpen,
    openAddExpense,
    openCreate,
    openExtract,
    openEdit,
    loading,
    activeSheet,
    createForm,
    addExpenseMode,
    selectedExistingSheetId,
    addExpenseItems,
    drawerMode,
    drawerOriginal,
    drawerDraft,
  ]);

  /* ===================== Render ===================== */

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ p: 2 }}>
        {/* ✅ HEADER (Logo Left, Title Right) */}
        <Box padding={4}>
          <Box className="crm-page-header" display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <img className="crm-primary-logo" src="/assets/rido-sports-logo.png" alt="Rido Sports" />

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CurrencyRupeeIcon sx={{ color: cornflowerBlue }} />
              <Typography variant="h5" fontWeight="bold" sx={{ fontFamily: "Montserrat, sans-serif" }}>
                Costing
              </Typography>

              {loading ? (
                <Typography sx={{ fontSize: 12, opacity: 0.7, ml: 1 }}>
                  Loading…
                  {submitProgress?.total ? (
                    <span>
                      {" "}
                      ({submitProgress.done}/{submitProgress.total})
                    </span>
                  ) : null}
                </Typography>
              ) : null}
            </Box>
          </Box>

          <Box className="crm-action-bar" sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={async () => {
                await refreshFast();
                if (mainTab === "lineSearch") {
                  setLineSearchRows([]);
                  setLineSearchTotalCount(0);
                  setLineSearchServerTotals({ qty: 0, amount: 0, gst: 0, total: 0 });
                  setLineSearchServerGroupedTotals([]);
                  setLineSearchLoaded(false);
                  setLineSearchSubmitted(false);
                }
              }}
              sx={{ fontFamily: "Montserrat, sans-serif" }}
            >
              Refresh
            </Button>

            <Button
              variant="outlined"
              onClick={() => {
                setOpenExtract(true);
                setExtractColsTouched(false);
              }}
              sx={{ fontFamily: "Montserrat, sans-serif" }}
            >
              Extract
            </Button>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openAddExpenseModal}
              sx={{ bgcolor: "#1f2a44", fontFamily: "Montserrat, sans-serif" }}
            >
              Add Expense
            </Button>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreateModal}
              sx={{ bgcolor: cornflowerBlue, fontFamily: "Montserrat, sans-serif" }}
            >
              Add Cost Sheet
            </Button>
          </Box>

          <Typography className="crm-keyboard-shortcuts" sx={{ fontSize: 11, opacity: 0.75, mt: 1 }}>
            Shortcuts: Ctrl+Shift+E (Add Expense) • Ctrl+Shift+C (Create Cost Sheet) • Ctrl+Enter
            (Submit/Save) • Esc (Close)
          </Typography>
        </Box>

        <Paper sx={{ mb: 1.5, borderRadius: 2, border: "1px solid #eee" }}>
          <Tabs
            value={mainTab}
            onChange={(_, value) => setMainTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 42,
              "& .MuiTab-root": {
                minHeight: 42,
                fontFamily: "Montserrat, sans-serif",
                fontWeight: 800,
                fontSize: 12,
              },
            }}
          >
            <Tab value="sheets" label="Cost Sheets" />
            <Tab value="lineSearch" label="Line Item Search" />
          </Tabs>
        </Paper>

        {mainTab === "sheets" ? (
        <Paper sx={{ p: 1.5, borderRadius: 2, border: "1px solid #eee" }}>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
            <TextField
              size="small"
              label="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 240 }}
            />

            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                {["Draft", "Final", "Archived"].map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Linked Entity Type</InputLabel>
              <Select
                value={entityTypeFilter}
                label="Linked Entity Type"
                onChange={(e) => setEntityTypeFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {["Account", "Deal", "Project", "Order"].map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <IconButton onClick={openColumns}>
              <ViewColumnIcon />
            </IconButton>

            <Typography sx={{ fontSize: 11, opacity: 0.72 }}>
              Showing {visibleCostSheets.length} of {filtered.length} cost sheet(s)
              {filtered.length > visibleCostSheets.length ? " — refine search to narrow results" : ""}
            </Typography>

            <Popover
              open={Boolean(columnsOpen)}
              anchorEl={columnsOpen}
              onClose={closeColumns}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            >
              <Box sx={{ p: 1.5 }}>
                <Typography sx={{ fontWeight: 800, fontSize: 12, mb: 1 }}>Columns</Typography>
                <Typography sx={{ fontSize: 11, opacity: 0.7, mb: 1 }}>
                  Summary columns are loaded for faster table rendering.
                </Typography>
                <FormGroup>
                  {costSheetColumns.filter((c) => !String(c).startsWith("__")).map((c) => (
                    <FormControlLabel
                      key={c}
                      control={
                        <Checkbox
                          size="small"
                          checked={visibleCols[c] === true || (COST_SHEET_OVERVIEW_FIELDS.includes(c) && visibleCols[c] !== false)}
                          onChange={(e) => setVisibleCols((p) => ({ ...p, [c]: e.target.checked }))}
                        />
                      }
                      label={<Typography sx={{ fontSize: 12 }}>{c}</Typography>}
                    />
                  ))}
                </FormGroup>
              </Box>
            </Popover>
          </Box>

          <Divider sx={{ my: 1 }} />

          <TableContainer className="crm-table-shell">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: "#f6f9ff" }}>
                  {visibleCostSheetColumns.map((c) => (
                    <TableCell key={c} sx={{ fontWeight: 800, fontSize: 12 }}>
                      {c}
                    </TableCell>
                  ))}
                  <TableCell sx={{ fontWeight: 800, fontSize: 12 }}>Action</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {visibleCostSheets.map((r, idx) => (
                  <TableRow key={String(r["Cost Sheet ID"] || idx)} hover>
                    {visibleCostSheetColumns.map((c) => (
                      <TableCell key={c} sx={{ fontSize: 12 }}>
                        {formatDateCell(c, r[c])}
                      </TableCell>
                    ))}
                    <TableCell>
                      <IconButton onClick={() => openEditModalById(r["Cost Sheet ID"])}>
                        <EditIcon sx={{ color: cornflowerBlue }} />
                      </IconButton>

                      <IconButton
                        size="small"
                        title="Download Cost Sheet"
                        onClick={() => downloadActiveCostSheetCsv(r)}
                        disabled={Boolean(downloadingCostSheetId)}
                      >
                        {downloadingCostSheetId === String(r?.["Cost Sheet ID"] || "").trim() ? (
                          <CircularProgress size={16} />
                        ) : (
                          <DownloadIcon fontSize="small" />
                        )}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}

                {!visibleCostSheets.length ? (
                  <TableRow>
                    <TableCell colSpan={visibleCostSheetColumns.length + 1} sx={{ fontSize: 12, opacity: 0.7 }}>
                      No cost sheets found.
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
        ) : null}

        {mainTab === "lineSearch" ? (
          <Paper sx={{ p: 1.5, borderRadius: 2, border: "1px solid #eee" }}>
            <Box
              sx={{
                display: "flex",
                gap: 0.75,
                flexWrap: "wrap",
                alignItems: "center",
                "& .MuiFormControl-root": {
                  flex: "0 1 auto",
                },
                "& .MuiInputBase-input, & .MuiSelect-select": {
                  fontSize: 12,
                },
                "& .MuiInputLabel-root": {
                  fontSize: 12,
                },
              }}
            >
              <LineItemSearchBox
                initialValue={lineSearch}
                loading={lineSearchLoading}
                disabled={loading}
                onSubmit={runLineItemSearch}
              />

              <FormControl size="small" sx={{ minWidth: 145, maxWidth: 180 }}>
                <InputLabel>Column</InputLabel>
                <Select
                  value={lineSearchColumn}
                  label="Column"
                  onChange={(e) => setLineSearchColumn(e.target.value)}
                  MenuProps={{ PaperProps: { sx: { "& .MuiMenuItem-root": { fontSize: 12 } } } }}
                >
                  <MenuItem value="all">All Columns</MenuItem>
                  {lineSearchAllColumns.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 125, maxWidth: 150 }}>
                <InputLabel>Match</InputLabel>
                <Select
                  value={lineSearchMatchMode}
                  label="Match"
                  onChange={(e) => setLineSearchMatchMode(e.target.value)}
                  MenuProps={{ PaperProps: { sx: { "& .MuiMenuItem-root": { fontSize: 12 } } } }}
                >
                  <MenuItem value="contains">Contains</MenuItem>
                  <MenuItem value="exact">Exact</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 125, maxWidth: 150 }}>
                <InputLabel>Entity</InputLabel>
                <Select
                  value={lineSearchEntityType}
                  label="Entity"
                  onChange={(e) => setLineSearchEntityType(e.target.value)}
                  MenuProps={{ PaperProps: { sx: { "& .MuiMenuItem-root": { fontSize: 12 } } } }}
                >
                  <MenuItem value="">All</MenuItem>
                  {["Account", "Deal", "Project", "Order"].map((t) => (
                    <MenuItem key={t} value={t}>
                      {t}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 145, maxWidth: 170 }}>
                <InputLabel>Payment</InputLabel>
                <Select
                  value={lineSearchPaymentStatus}
                  label="Payment"
                  onChange={(e) => setLineSearchPaymentStatus(e.target.value)}
                  MenuProps={{ PaperProps: { sx: { "& .MuiMenuItem-root": { fontSize: 12 } } } }}
                >
                  <MenuItem value="">All</MenuItem>
                  {(validation.paymentStatus || DEFAULT_PAYMENT_STATUSES).map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 125, maxWidth: 160 }}>
                <InputLabel>Head</InputLabel>
                <Select
                  value={lineSearchHead}
                  label="Head"
                  onChange={(e) => setLineSearchHead(e.target.value)}
                  MenuProps={{ PaperProps: { sx: { "& .MuiMenuItem-root": { fontSize: 12 } } } }}
                >
                  <MenuItem value="">All</MenuItem>
                  {(validation.heads || []).map((h) => (
                    <MenuItem key={h} value={h}>
                      {h}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 145, maxWidth: 175 }}>
                <InputLabel>Total By</InputLabel>
                <Select
                  value={lineSearchGroupBy}
                  label="Total By"
                  onChange={(e) => setLineSearchGroupBy(e.target.value)}
                  MenuProps={{ PaperProps: { sx: { "& .MuiMenuItem-root": { fontSize: 12 } } } }}
                >
                  {lineSearchGroupOptions.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <IconButton size="small" onClick={openLineSearchColumns} title="Line item columns">
                <ViewColumnIcon />
              </IconButton>

              <Popover
                open={Boolean(lineSearchColumnsAnchor)}
                anchorEl={lineSearchColumnsAnchor}
                onClose={closeLineSearchColumns}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              >
                <Box sx={{ p: 1.5, maxWidth: 420 }}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                    <Typography sx={{ fontWeight: 900, fontSize: 12 }}>Line Item Columns</Typography>

                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button
                        size="small"
                        onClick={() => {
                          const all = {};
                          lineSearchAllColumns.forEach((c) => (all[c] = true));
                          setLineSearchVisibleCols(all);
                        }}
                      >
                        Select All
                      </Button>
                      <Button
                        size="small"
                        onClick={() => {
                          const defaults = {};
                          lineSearchAllColumns.forEach((c) => {
                            defaults[c] = lineSearchDefaultCols.includes(c);
                          });
                          setLineSearchVisibleCols(defaults);
                        }}
                      >
                        Default
                      </Button>
                    </Box>
                  </Box>

                  <Divider sx={{ mb: 1 }} />

                  <FormGroup>
                    {lineSearchAllColumns.map((c) => (
                      <FormControlLabel
                        key={c}
                        control={
                          <Checkbox
                            size="small"
                            checked={lineSearchVisibleCols[c] !== false}
                            onChange={(e) =>
                              setLineSearchVisibleCols((p) => ({ ...p, [c]: e.target.checked }))
                            }
                          />
                        }
                        label={<Typography sx={{ fontSize: 12 }}>{c}</Typography>}
                      />
                    ))}
                  </FormGroup>
                </Box>
              </Popover>
            </Box>

            <Grid container spacing={1} sx={{ mt: 1 }}>
              {[
                ["Matches", lineSearchTotals.count],
                ["QTY", fmtINR(lineSearchTotals.qty)],
                ["Amount", `₹ ${fmtINR(lineSearchTotals.amount)}`],
                ["GST", `₹ ${fmtINR(lineSearchTotals.gst)}`],
                ["Total Amount", `₹ ${fmtINR(lineSearchTotals.total)}`],
              ].map(([label, value]) => (
                <Grid item xs={6} sm={4} md={2.4} key={label}>
                  <Paper
                    variant="outlined"
                    sx={{ p: 1, borderRadius: 2, borderColor: "#e9eefc", bgcolor: "#fbfcff" }}
                  >
                    <Typography sx={{ fontSize: 11, opacity: 0.7 }}>{label}</Typography>
                    <Typography sx={{ fontWeight: 900, fontSize: 13 }}>{value}</Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            <Grid container spacing={1.25} sx={{ mt: 0.5 }}>
              <Grid item xs={12} md={4}>
                <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
                  <Box sx={{ p: 1, bgcolor: "#f6f9ff", borderBottom: "1px solid #e9eefc" }}>
                    <Typography sx={{ fontWeight: 900, fontSize: 12 }}>
                      Totals by {lineSearchGroupBy}
                    </Typography>
                    <Typography sx={{ fontSize: 11, opacity: 0.7 }}>
                      {lineSearchGroupedTotals.length} grouped result(s)
                    </Typography>
                  </Box>

                  <TableContainer sx={{ maxHeight: 420 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 900, fontSize: 11 }}>{lineSearchGroupBy}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 900, fontSize: 11 }}>
                            Total
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 900, fontSize: 11 }}>
                            Count
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {lineSearchGroupedTotals.map((row) => (
                          <TableRow key={row.key} hover>
                            <TableCell sx={{ fontSize: 11 }}>{row.key}</TableCell>
                            <TableCell align="right" sx={{ fontSize: 11, fontWeight: 800 }}>
                              ₹ {fmtINR(row.total)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: 11 }}>
                              {row.count}
                            </TableCell>
                          </TableRow>
                        ))}

                        {!lineSearchGroupedTotals.length ? (
                          <TableRow>
                            <TableCell colSpan={3} sx={{ fontSize: 12, opacity: 0.7 }}>
                              {lineSearchLoading ? "Loading line items..." : "No grouped totals found."}
                            </TableCell>
                          </TableRow>
                        ) : null}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>

              <Grid item xs={12} md={8}>
                <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
                  <Box
                    sx={{
                      p: 1,
                      bgcolor: "#f6f9ff",
                      borderBottom: "1px solid #e9eefc",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <Box>
                      <Typography sx={{ fontWeight: 900, fontSize: 12 }}>Matching Line Items</Typography>
                      <Typography sx={{ fontSize: 11, opacity: 0.7 }}>
                        {lineSearchLoading
                          ? "Loading line items..."
                          : !lineSearchSubmitted
                          ? "Enter a value or select filters, then click Search"
                          : `${lineSearchTotalCount} match(es) | showing ${visibleLineSearchRows.length}`}
                      </Typography>
                    </Box>

                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<DownloadIcon />}
                      onClick={downloadLineSearchCsv}
                      disabled={!filteredLineSearchRows.length}
                      sx={{ fontFamily: "Montserrat, sans-serif", fontSize: 11 }}
                    >
                      CSV
                    </Button>
                  </Box>

                  <TableContainer sx={{ maxHeight: 520 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          {lineSearchDisplayCols.map((c) => (
                            <TableCell key={c} sx={{ fontWeight: 900, fontSize: 11, whiteSpace: "nowrap" }}>
                              {c}
                            </TableCell>
                          ))}
                          <TableCell sx={{ fontWeight: 900, fontSize: 11, whiteSpace: "nowrap" }}>
                            Action
                          </TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {visibleLineSearchRows.map((row, idx) => (
                          <TableRow key={row.__lineKey || idx} hover>
                            {lineSearchDisplayCols.map((c) => (
                              <TableCell key={c} sx={{ fontSize: 11, verticalAlign: "top" }}>
                                {formatDateCell(c, getLineSearchValue(row, c))}
                              </TableCell>
                            ))}
                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              <IconButton
                                size="small"
                                title="Edit Line Item"
                                onClick={() => openLineItemFromSearch(row)}
                              >
                                <EditIcon sx={{ color: cornflowerBlue }} fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}

                        {!filteredLineSearchRows.length ? (
                          <TableRow>
                            <TableCell
                              colSpan={lineSearchDisplayCols.length + 1}
                              sx={{ fontSize: 12, opacity: 0.7 }}
                            >
                              {lineSearchLoading
                                ? "Loading line items..."
                                : !lineSearchSubmitted
                                ? "Enter a value or select filters, then click Search."
                                : lineSearchLoaded
                                ? "No line items match the current search."
                                : "Click Search to load line items."}
                            </TableCell>
                          </TableRow>
                        ) : null}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>
            </Grid>
          </Paper>
        ) : null}

        {/* ================= EXTRACT MODAL ================= */}
        <Dialog
          open={openExtract}
          onClose={(_, reason) => reason !== "backdropClick" && setOpenExtract(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 800 }}>Extract Costing Data</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <Paper
                  variant="outlined"
                  sx={{ p: 1.2, borderRadius: 2, borderColor: "#e9eefc", bgcolor: "#fbfcff" }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Box>
                      <Typography sx={{ fontWeight: 900, fontSize: 12 }}>Columns to Export</Typography>
                      <Typography sx={{ fontSize: 11, opacity: 0.75 }}>
                        Selected:{" "}
                        {selectedExtractFields.length
                          ? `${selectedExtractFields.length} / ${extractAllColumns.length}`
                          : `All (${extractAllColumns.length})`}
                      </Typography>
                    </Box>

                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<ViewColumnIcon />}
                      onClick={openExtractColumns}
                      sx={{ fontFamily: "Montserrat, sans-serif" }}
                    >
                      Select Columns
                    </Button>
                  </Box>

                  <Popover
                    open={Boolean(extractColumnsAnchor)}
                    anchorEl={extractColumnsAnchor}
                    onClose={closeExtractColumns}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                  >
                    <Box sx={{ p: 1.5, maxWidth: 420 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          mb: 1,
                        }}
                      >
                        <Typography sx={{ fontWeight: 900, fontSize: 12 }}>Export Columns</Typography>

                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Button
                            size="small"
                            onClick={() => {
                              const all = {};
                              (extractAllColumns || []).forEach((c) => (all[c] = true));
                              setExtractVisibleCols(all);
                              setExtractColsTouched(true);
                            }}
                          >
                            Select All
                          </Button>
                          <Button
                            size="small"
                            onClick={() => {
                              const none = {};
                              (extractAllColumns || []).forEach((c) => (none[c] = false));
                              setExtractVisibleCols(none);
                              setExtractColsTouched(true);
                            }}
                          >
                            Clear
                          </Button>
                        </Box>
                      </Box>

                      <Divider sx={{ mb: 1 }} />

                      <FormGroup>
                        {(extractAllColumns || []).map((c) => (
                          <FormControlLabel
                            key={c}
                            control={
                              <Checkbox
                                size="small"
                                checked={extractVisibleCols[c] !== false}
                                onChange={(e) => {
                                  const checked = e.target.checked;

                                  setExtractVisibleCols((prev) => {
                                    const next = { ...(prev || {}), [c]: checked };
                                    const userKey = String(loggedInName || "").trim();
                                    const storageKey = getExtractColsStorageKey(userKey);
                                    try {
                                      localStorage.setItem(storageKey, JSON.stringify(next));
                                    } catch {}
                                    return next;
                                  });

                                  setExtractColsTouched(true);
                                }}
                              />
                            }
                            label={<Typography sx={{ fontSize: 12 }}>{c}</Typography>}
                          />
                        ))}
                      </FormGroup>

                      <Divider sx={{ mt: 1 }} />

                      <Typography sx={{ fontSize: 11, opacity: 0.75, mt: 1 }}>
                        If you keep all selected, export will behave as “export all columns”.
                      </Typography>
                    </Box>
                  </Popover>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Export Type</InputLabel>
                  <Select
                    label="Export Type"
                    value={extractForm.exportType}
                    onChange={(e) => {
                      const nextType = e.target.value;
                      setExtractForm((p) => ({
                        ...p,
                        exportType: nextType,
                        subtotalBy: nextType === "finance" ? (p.subtotalBy || "none") : "none",
                      }));
                    }}
                  >
                    <MenuItem value="costing">Costing Export</MenuItem>
                    <MenuItem value="finance">Finance Export (Subtotals)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small" disabled={extractForm.exportType !== "finance"}>
                  <InputLabel>Subtotal By</InputLabel>
                  <Select
                    label="Subtotal By"
                    value={extractForm.subtotalBy}
                    onChange={(e) => setExtractForm((p) => ({ ...p, subtotalBy: e.target.value }))}
                  >
                    <MenuItem value="none">None</MenuItem>
                    {financeSubtotalOptions.map((c) => (
                      <MenuItem key={c} value={c}>
                        {c}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {extractForm.exportType === "finance" ? (
                  <Typography sx={{ fontSize: 11, opacity: 0.72, mt: 0.5 }}>
                    Subtotal dropdown shows only the columns you selected in “Select Columns”.
                  </Typography>
                ) : null}
              </Grid>

              <Grid item xs={12}>
                <Autocomplete
                  size="small"
                  options={costSheetOptions}
                  value={selectedExtractCostSheetOption}
                  isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                  getOptionLabel={(opt) => String(opt?.label || opt?.id || "")}
                  onChange={(_, picked) => {
                    const id = picked ? String(picked.id) : "";
                    setExtractForm((p) => ({
                      ...p,
                      costSheetId: id,
                      entityType: "",
                      linkedEntityId: "",
                      from: "",
                      to: "",
                    }));
                  }}
                  renderInput={(params) => <TextField {...params} label="Existing Cost Sheet (Search)" />}
                />

                {extractForm.costSheetId ? (
                  <Typography sx={{ fontSize: 11, opacity: 0.75, mt: 0.5 }}>
                    Extract will be generated only for the selected cost sheet. Date/entity filters are disabled.
                  </Typography>
                ) : null}
              </Grid>

              {!extractForm.costSheetId ? (
                <>
                  <Grid item xs={12}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Linked Entity Type</InputLabel>
                      <Select
                        label="Linked Entity Type"
                        value={extractForm.entityType}
                        onChange={(e) => setExtractForm((p) => ({ ...p, entityType: e.target.value }))}
                      >
                        <MenuItem value="">All</MenuItem>
                        {["Account", "Deal", "Project", "Order"].map((t) => (
                          <MenuItem key={t} value={t}>
                            {t}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Linked Entity ID"
                      value={extractForm.linkedEntityId}
                      onChange={(e) => setExtractForm((p) => ({ ...p, linkedEntityId: e.target.value }))}
                    />
                  </Grid>
                </>
              ) : null}

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Particular"
                  value={extractForm.particular}
                  onChange={(e) => setExtractForm((p) => ({ ...p, particular: e.target.value }))}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Payment Status</InputLabel>
                  <Select
                    label="Payment Status"
                    value={extractForm.paymentStatus}
                    onChange={(e) => setExtractForm((p) => ({ ...p, paymentStatus: e.target.value }))}
                  >
                    <MenuItem value="">All</MenuItem>
                    {(validation.paymentStatus || DEFAULT_PAYMENT_STATUSES).map((s) => (
                      <MenuItem key={s} value={s}>
                        {s}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {!extractForm.costSheetId ? (
                <>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="From Date"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      value={extractForm.from}
                      onChange={(e) => setExtractForm((p) => ({ ...p, from: e.target.value }))}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="To Date"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      value={extractForm.to}
                      onChange={(e) => setExtractForm((p) => ({ ...p, to: e.target.value }))}
                    />
                  </Grid>
                </>
              ) : null}

              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Format</InputLabel>
                  <Select
                    label="Format"
                    value={extractForm.format}
                    onChange={(e) => setExtractForm((p) => ({ ...p, format: e.target.value }))}
                  >
                    <MenuItem value="csv">CSV</MenuItem>
                    <MenuItem value="xlsx">Excel (XLSX)</MenuItem>
                    <MenuItem value="pdf">PDF</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small" disabled={extractForm.format === "csv"}>
                  <InputLabel>Merge By</InputLabel>
                  <Select
                    label="Merge By"
                    value={extractForm.mergeBy}
                    onChange={(e) => setExtractForm((p) => ({ ...p, mergeBy: e.target.value }))}
                  >
                    <MenuItem value="none">None</MenuItem>
                    <MenuItem value="Particular">Particular</MenuItem>
                    <MenuItem value="Head Name">Head Name</MenuItem>
                    <MenuItem value="Subcategory">Subcategory</MenuItem>
                    <MenuItem value="Linked Entity Name">Linked Entity Name</MenuItem>
                  </Select>
                </FormControl>
                <Typography sx={{ fontSize: 11, opacity: 0.72, mt: 0.5 }}>
                  Merge is available for Excel and PDF exports only. CSV does not support merged cells or borders.
                </Typography>
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setOpenExtract(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={triggerExtraction}
              sx={{ bgcolor: cornflowerBlue }}
              disabled={extractForm.exportType === "finance" && !extractForm.subtotalBy}
            >
              Download
            </Button>
          </DialogActions>
        </Dialog>

        {/* ================= ADD EXPENSE MODAL ================= */}
        <Dialog
          open={openAddExpense}
          onClose={(_, reason) => reason !== "backdropClick" && setOpenAddExpense(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 800 }}>Add Expense</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Mode</InputLabel>
                  <Select label="Mode" value={addExpenseMode} onChange={(e) => setAddExpenseMode(e.target.value)}>
                    <MenuItem value="existing">Add to Existing Cost Sheet</MenuItem>
                    <MenuItem value="new">Create New Cost Sheet</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper
                  variant="outlined"
                  sx={{
                    height: "100%",
                    p: 1.1,
                    borderRadius: 2,
                    borderColor: "#e9eefc",
                    bgcolor: "#fbfcff",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    gap: 0.4,
                  }}
                >
                  <Typography sx={{ fontWeight: 900, fontSize: 12 }}>Batch Summary</Typography>
                  <Typography sx={{ fontSize: 12, opacity: 0.8 }}>
                    Line items: {addExpenseTotals.count} | Total: ₹ {fmtINR(addExpenseTotals.total)}
                    {submitProgress?.total ? (
                      <span>
                        {" "}
                        | Submitting: {submitProgress.done}/{submitProgress.total}
                      </span>
                    ) : null}
                  </Typography>
                  <Typography sx={{ fontSize: 11, opacity: 0.7 }}>
                    Shortcuts: Ctrl+Enter (Submit) • Ctrl+Shift+A (Add Line Item) • Esc (Close)
                  </Typography>
                </Paper>
              </Grid>

              {addExpenseMode === "existing" ? (
                <>
                  <Grid item xs={12}>
                    <Autocomplete
                      size="small"
                      options={costSheetOptions}
                      value={selectedAddExpenseCostSheetOption}
                      isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                      getOptionLabel={(opt) => String(opt?.label || opt?.id || "")}
                      onChange={(_, picked) => {
                        const id = picked ? String(picked.id) : "";
                        setSelectedExistingSheetId(id);
                        loadAdvancesForCostSheet(id);
                      }}
                      renderInput={(params) => <TextField {...params} label="Existing Cost Sheet (Search)" />}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, borderColor: "#e9eefc", bgcolor: "#fbfcff" }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, flexWrap: "wrap", mb: 1 }}>
                        <Box>
                          <Typography sx={{ fontWeight: 900, fontSize: 12 }}>Advance Attribution</Typography>
                          <Typography sx={{ fontSize: 11, opacity: 0.72 }}>
                            Available advances: {advanceLoading ? "Loading..." : advanceOptions.length}
                          </Typography>
                        </Box>
                        <Button size="small" variant="outlined" onClick={() => loadAdvancesForCostSheet(selectedExistingSheetId)} disabled={!selectedExistingSheetId || advanceLoading}>
                          Refresh Advances
                        </Button>
                      </Box>

                      <Grid container spacing={1}>
                        <Grid item xs={12} md={2.5}>
                          <TextField
                            fullWidth
                            size="small"
                            type="date"
                            label="Advance Date"
                            InputLabelProps={{ shrink: true }}
                            value={advanceForm["Advance Date"]}
                            onChange={(e) => setAdvanceForm((p) => ({ ...p, "Advance Date": e.target.value }))}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Advance Paid To"
                            value={advanceForm["Advance Paid To"]}
                            onChange={(e) => setAdvanceForm((p) => ({ ...p, "Advance Paid To": e.target.value }))}
                          />
                        </Grid>
                        <Grid item xs={12} md={2.5}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Advance Amount"
                            value={advanceForm["Advance Amount"]}
                            onChange={(e) => setAdvanceForm((p) => ({ ...p, "Advance Amount": e.target.value }))}
                          />
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Mode"
                            value={advanceForm.Mode}
                            onChange={(e) => setAdvanceForm((p) => ({ ...p, Mode: e.target.value }))}
                          />
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Reference No"
                            value={advanceForm["Reference No"]}
                            onChange={(e) => setAdvanceForm((p) => ({ ...p, "Reference No": e.target.value }))}
                          />
                        </Grid>
                        <Grid item xs={12} md={12}>
                          <Button fullWidth variant="contained" onClick={recordAdvanceForSelectedSheet} disabled={!selectedExistingSheetId || advanceLoading} sx={{ height: 40, bgcolor: cornflowerBlue }}>
                            Record
                          </Button>
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Advance Remarks"
                            value={advanceForm.Remarks}
                            onChange={(e) => setAdvanceForm((p) => ({ ...p, Remarks: e.target.value }))}
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Owner"
                      value={createForm["Owner"] || ""}
                      onChange={(e) => setCreateForm((p) => ({ ...p, Owner: e.target.value }))}
                      InputProps={{ readOnly: !isAdmin }}
                      helperText={!isAdmin ? "Owner is locked to the logged-in user." : ""}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Linked Entity Type</InputLabel>
                      <Select
                        label="Linked Entity Type"
                        value={createForm["Linked Entity Type"] || ""}
                        onChange={async (e) => {
                          const type = e.target.value;
                          setCreateForm((p) => ({
                            ...p,
                            "Linked Entity Type": type,
                            "Linked Entity ID": "",
                            "Linked Entity Name": "",
                          }));
                          setEntityOptions([]);
                          await loadEntitiesForType(type);
                        }}
                      >
                        {["Account", "Deal", "Project", "Order"].map((t) => (
                          <MenuItem key={t} value={t}>
                            {t}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      size="small"
                      options={entityOptions || []}
                      loading={entityLoading}
                      value={selectedEntityOption}
                      getOptionLabel={entityOptionLabel}
                      isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                      disabled={!createForm["Linked Entity Type"]}
                      onChange={(_, picked) => {
                        const id = picked ? String(picked.id) : "";
                        setCreateForm((p) => ({
                          ...p,
                          "Linked Entity ID": id,
                          "Linked Entity Name": picked?.display || "",
                        }));
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Linked Entity Name (Search)"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {entityLoading ? <CircularProgress size={16} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Status</InputLabel>
                      <Select
                        label="Status"
                        value={createForm.Status || "Draft"}
                        onChange={(e) => setCreateForm((p) => ({ ...p, Status: e.target.value }))}
                      >
                        {["Draft", "Final", "Archived"].map((s) => (
                          <MenuItem key={s} value={s}>
                            {s}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Client Name"
                      value={createForm["Client Name"] || ""}
                      onChange={(e) => setCreateForm((p) => ({ ...p, "Client Name": e.target.value }))}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Project Type"
                      value={createForm["Project Type"] || ""}
                      onChange={(e) => setCreateForm((p) => ({ ...p, "Project Type": e.target.value }))}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Notes"
                      value={createForm.Notes || ""}
                      onChange={(e) => setCreateForm((p) => ({ ...p, Notes: e.target.value }))}
                      multiline
                      minRows={2}
                    />
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <Typography sx={{ fontWeight: 900, fontSize: 12 }}>Line Items</Typography>

                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={addAnotherExpenseItem}
                    sx={{ fontFamily: "Montserrat, sans-serif" }}
                  >
                    Add Another Line Item
                  </Button>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                  {(addExpenseItems || []).map((it, idx) => {
                    const hasQtyRate = Boolean(it?.__hasQtyRate);
                    const canManualTotal = !hasQtyRate;

                    return (
                      <Paper
                        key={idx}
                        variant="outlined"
                        sx={{
                          p: 1.25,
                          borderRadius: 2,
                          borderColor: "#e9eefc",
                          bgcolor: "#ffffff",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1,
                            mb: 1,
                          }}
                        >
                          <Typography sx={{ fontWeight: 900, fontSize: 12 }}>
                            Item #{idx + 1} — Total: ₹ {fmtINR(safeNum(it?.["Total Amount"]))}
                          </Typography>

                          <IconButton
                            size="small"
                            onClick={() => removeExpenseItem(idx)}
                            disabled={(addExpenseItems || []).length <= 1}
                            title={
                              (addExpenseItems || []).length <= 1
                                ? "At least one line item is required"
                                : "Remove this line item"
                            }
                          >
                            <RemoveCircleOutlineIcon
                              sx={{ color: (addExpenseItems || []).length <= 1 ? "#bbb" : "#c62828" }}
                            />
                          </IconButton>
                        </Box>

                        <Grid container spacing={1.25}>
                          <Grid item xs={12} md={6}>
                            <FormControl size="small" fullWidth>
                              <InputLabel>Head Name</InputLabel>
                              <Select
                                label="Head Name"
                                value={it?.["Head Name"] || ""}
                                onChange={(e) => setAddExpenseItemField(idx, "Head Name", e.target.value)}
                              >
                                {(validation.heads || []).map((h) => (
                                  <MenuItem key={h} value={h}>
                                    {h}
                                  </MenuItem>
                                ))}
                                {!validation.heads?.length ? (
                                  <MenuItem value="" disabled>
                                    No heads found
                                  </MenuItem>
                                ) : null}
                              </Select>
                            </FormControl>
                          </Grid>

                          <Grid item xs={12} md={6}>
                            <FormControl size="small" fullWidth>
                              <InputLabel>Subcategory</InputLabel>
                              <Select
                                label="Subcategory"
                                value={it?.Subcategory || ""}
                                onChange={(e) => setAddExpenseItemField(idx, "Subcategory", e.target.value)}
                              >
                                {subcatsForHead(it?.["Head Name"] || "").map((s) => (
                                  <MenuItem key={s} value={s}>
                                    {s}
                                  </MenuItem>
                                ))}
                                {!subcatsForHead(it?.["Head Name"] || "").length ? (
                                  <MenuItem value="" disabled>
                                    No subcategories
                                  </MenuItem>
                                ) : null}
                              </Select>
                            </FormControl>
                          </Grid>

                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Expense Date"
                              type="date"
                              InputLabelProps={{ shrink: true }}
                              value={it?.["Expense Date"] || ""}
                              onChange={(e) => setAddExpenseItemField(idx, "Expense Date", e.target.value)}
                            />
                          </Grid>

                          <Grid item xs={12} md={6}>
                            <FormControl size="small" fullWidth>
                              <InputLabel>Payment Status</InputLabel>
                              <Select
                                label="Payment Status"
                                value={it?.["Payment Status"] || ""}
                                onChange={(e) => setAddExpenseItemField(idx, "Payment Status", e.target.value)}
                              >
                                {(validation.paymentStatus || DEFAULT_PAYMENT_STATUSES).map((s) => (
                                  <MenuItem key={s} value={s}>
                                    {s}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>

                          <Grid item xs={12} md={4}>
                            <FormControl size="small" fullWidth>
                              <InputLabel>Payment Source</InputLabel>
                              <Select
                                label="Payment Source"
                                value={it?.["Payment Source"] || "Company Paid"}
                                onChange={(e) => setAddExpenseItemField(idx, "Payment Source", e.target.value)}
                              >
                                {["Company Paid", "Advance", "Pending"].map((s) => (
                                  <MenuItem key={s} value={s}>
                                    {s}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>

                          <Grid item xs={12} md={5}>
                            <FormControl size="small" fullWidth disabled={it?.["Payment Source"] !== "Advance" || !selectedExistingSheetId}>
                              <InputLabel>Advance Reference</InputLabel>
                              <Select
                                label="Advance Reference"
                                value={it?.["Advance ID"] || ""}
                                onChange={(e) => setAddExpenseItemField(idx, "Advance ID", e.target.value)}
                              >
                                {advanceOptions.map((adv) => (
                                  <MenuItem key={adv["Advance ID"]} value={adv["Advance ID"]}>
                                    {adv.label || `${adv["Advance ID"]} | Balance ₹ ${fmtINR(safeNum(adv["Balance Available"]))}`}
                                  </MenuItem>
                                ))}
                                {!advanceOptions.length ? (
                                  <MenuItem value="" disabled>
                                    No advances for selected cost sheet
                                  </MenuItem>
                                ) : null}
                              </Select>
                            </FormControl>
                          </Grid>

                          <Grid item xs={12} md={3}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Advance Used"
                              value={it?.["Advance Applied Amount"] || ""}
                              onChange={(e) => setAddExpenseItemField(idx, "Advance Applied Amount", e.target.value)}
                              disabled={it?.["Payment Source"] !== "Advance"}
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Particular"
                              value={it?.Particular || ""}
                              onChange={(e) => setAddExpenseItemField(idx, "Particular", e.target.value)}
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Details"
                              value={it?.Details || ""}
                              onChange={(e) => setAddExpenseItemField(idx, "Details", e.target.value)}
                              multiline
                              minRows={2}
                            />
                          </Grid>

                          <Grid item xs={4}>
                            <TextField
                              fullWidth
                              size="small"
                              label="QTY"
                              value={it?.["QTY"] || ""}
                              onChange={(e) => setAddExpenseItemField(idx, "QTY", e.target.value)}
                            />
                          </Grid>
                          <Grid item xs={4}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Rate"
                              value={it?.["Rate"] || ""}
                              onChange={(e) => setAddExpenseItemField(idx, "Rate", e.target.value)}
                            />
                          </Grid>
                          <Grid item xs={4}>
                            <TextField
                              fullWidth
                              size="small"
                              label="GST %"
                              value={it?.["GST %"] || ""}
                              onChange={(e) => setAddExpenseItemField(idx, "GST %", e.target.value)}
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  size="small"
                                  checked={Boolean(it?.__useAutoAmount) && hasQtyRate}
                                  disabled={!hasQtyRate}
                                  onChange={(e) => setAddExpenseItemField(idx, "__useAutoAmount", e.target.checked)}
                                />
                              }
                              label={<Typography sx={{ fontSize: 12 }}>Use Auto Amount (QTY × Rate)</Typography>}
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Amount"
                              value={it?.["Amount"] || ""}
                              onChange={(e) => setAddExpenseItemField(idx, "Amount", e.target.value)}
                              InputProps={{ readOnly: Boolean(it?.__useAutoAmount) && hasQtyRate }}
                              helperText={
                                hasQtyRate
                                  ? Boolean(it?.__useAutoAmount)
                                    ? "Auto (QTY × Rate)"
                                    : "Manual"
                                  : "Derived from Total Amount (if Total is entered) or manual"
                              }
                            />
                          </Grid>

                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              size="small"
                              label="GST Amount"
                              value={it?.["GST Amount"] || ""}
                              InputProps={{ readOnly: true }}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Total Amount"
                              value={it?.["Total Amount"] || ""}
                              onChange={(e) => setAddExpenseItemField(idx, "Total Amount", e.target.value)}
                              InputProps={{ readOnly: !canManualTotal }}
                              helperText={
                                canManualTotal
                                  ? "Manual Total (when QTY×Rate is unavailable)"
                                  : "Auto (Amount + GST Amount)"
                              }
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Attachment Link"
                              value={it?.["Attachment Link"] || ""}
                              onChange={(e) => setAddExpenseItemField(idx, "Attachment Link", e.target.value)}
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Voucher/Invoice No"
                              value={it?.["Voucher/Invoice No"] || ""}
                              onChange={(e) => setAddExpenseItemField(idx, "Voucher/Invoice No", e.target.value)}
                            />
                          </Grid>
                        </Grid>
                      </Paper>
                    );
                  })}
                </Box>
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setOpenAddExpense(false)}>Cancel</Button>
            <Button variant="contained" onClick={submitAddExpense} sx={{ bgcolor: cornflowerBlue }} disabled={loading}>
              {loading ? "Submitting…" : "Submit"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ================= CREATE COST SHEET MODAL ================= */}
        <Dialog
          open={openCreate}
          onClose={(_, reason) => reason !== "backdropClick" && setOpenCreate(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 800 }}>Create Cost Sheet</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Owner"
                  value={createForm["Owner"] || ""}
                  onChange={(e) => setCreateForm((p) => ({ ...p, Owner: e.target.value }))}
                  InputProps={{ readOnly: !isAdmin }}
                  helperText={!isAdmin ? "Owner is locked to the logged-in user." : ""}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Linked Entity Type</InputLabel>
                  <Select
                    label="Linked Entity Type"
                    value={createForm["Linked Entity Type"] || ""}
                    onChange={async (e) => {
                      const type = e.target.value;
                      setCreateForm((p) => ({
                        ...p,
                        "Linked Entity Type": type,
                        "Linked Entity ID": "",
                        "Linked Entity Name": "",
                      }));
                      setEntityOptions([]);
                      await loadEntitiesForType(type);
                    }}
                  >
                    {["Account", "Deal", "Project", "Order"].map((t) => (
                      <MenuItem key={t} value={t}>
                        {t}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <Autocomplete
                  size="small"
                  options={entityOptions || []}
                  loading={entityLoading}
                  value={selectedEntityOption}
                  getOptionLabel={entityOptionLabel}
                  isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                  disabled={!createForm["Linked Entity Type"]}
                  onChange={(_, picked) => {
                    const id = picked ? String(picked.id) : "";
                    setCreateForm((p) => ({
                      ...p,
                      "Linked Entity ID": id,
                      "Linked Entity Name": picked?.display || "",
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Linked Entity Name (Search)"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {entityLoading ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Linked Entity ID"
                  value={createForm["Linked Entity ID"] || ""}
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Client Name"
                  value={createForm["Client Name"] || ""}
                  onChange={(e) => setCreateForm((p) => ({ ...p, "Client Name": e.target.value }))}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Project Type"
                  value={createForm["Project Type"] || ""}
                  onChange={(e) => setCreateForm((p) => ({ ...p, "Project Type": e.target.value }))}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={createForm.Status || "Draft"}
                    onChange={(e) => setCreateForm((p) => ({ ...p, Status: e.target.value }))}
                  >
                    {["Draft", "Final", "Archived"].map((s) => (
                      <MenuItem key={s} value={s}>
                        {s}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Notes"
                  value={createForm.Notes || ""}
                  onChange={(e) => setCreateForm((p) => ({ ...p, Notes: e.target.value }))}
                  multiline
                  minRows={3}
                />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setOpenCreate(false)}>Cancel</Button>
            <Button variant="contained" onClick={createCostSheet} sx={{ bgcolor: cornflowerBlue }} disabled={loading}>
              {loading ? "Creating…" : "Create"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ================= EDIT COST SHEET (TABLE + RIGHT DRAWER) ================= */}
        <Dialog
          open={openEdit}
          onClose={(_, reason) => reason !== "backdropClick" && setOpenEdit(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 800 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.4 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Typography sx={{ fontWeight: 900 }}>
                  Cost Sheet — {activeSheet?.["Cost Sheet ID"] || ""}
                </Typography>

                {activeSheet?.["Status"] ? (
                  <Typography
                    sx={{
                      fontSize: 11,
                      px: 1,
                      py: 0.25,
                      borderRadius: 1,
                      bgcolor: "#eef4ff",
                      border: "1px solid #d9e4ff",
                      fontWeight: 800,
                    }}
                  >
                    {String(activeSheet["Status"])}
                  </Typography>
                ) : null}
              </Box>

              <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: "#1f2a44" }}>
                {activeSheet?.["Linked Entity Name"] || "—"}
              </Typography>

              <Typography sx={{ fontSize: 12, opacity: 0.75 }}>
                Linked: {activeSheet?.["Linked Entity Type"] || ""} — {activeSheet?.["Linked Entity ID"] || ""}
                {"  "} | Owner: {activeSheet?.["Owner"] || ""}
                {"  "} | Client: {activeSheet?.["Client Name"] || "—"}
                {"  "} | Project Type: {activeSheet?.["Project Type"] || "—"}
              </Typography>

              <Typography sx={{ fontSize: 11, opacity: 0.7 }}>
                Shortcuts: Ctrl+Enter (Save) • Ctrl+Shift+Enter (Save & New) • Esc (Close Drawer/Modal)
              </Typography>
            </Box>
          </DialogTitle>

          <DialogContent dividers sx={{ position: "relative" }}>
            <Paper
              sx={{
                p: 1.2,
                mb: 1.2,
                borderRadius: 2,
                border: "1px solid #eee",
                position: "sticky",
                top: 0,
                zIndex: 2,
                background: "#fff",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: { xs: "stretch", md: "center" },
                  justifyContent: "space-between",
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                <Box sx={{ minWidth: 240, flex: "1 1 280px" }}>
                  <Typography sx={{ fontWeight: 900, fontSize: 12 }}>
                    Overall Total (Active Items): ₹ {fmtINR(displayedGrandTotal)}
                  </Typography>
                  {lineItemsRequested ? (
                    <Typography sx={{ fontSize: 11, fontWeight: 800, color: "#1f2a44" }}>
                      Current Page Total: ₹ {fmtINR(displayedPageTotal)}
                    </Typography>
                  ) : null}
                  <Typography sx={{ fontSize: 11, opacity: 0.7 }}>
                    {!lineItemsRequested
                      ? "Line items are not loaded yet."
                      : detailsLoading
                      ? "Loading line items…"
                      : `${loadedLineItemCount} active line item(s) loaded${
                          loadedLineItemCount
                            ? ` (${lineItemsRangeStart}-${lineItemsRangeEnd} of ${lineItemsKnownTotal})`
                            : ""
                        }${
                          hiddenLineItemCount
                            ? ". Use Next to view more."
                            : ""
                        }`}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    flexWrap: "wrap",
                    justifyContent: { xs: "flex-start", md: "flex-end" },
                    alignItems: "center",
                    flex: "1 1 420px",
                  }}
                >
                  {lineItemsRequested ? (
                    <>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={detailsLoading || !hasPrevLineItemPage}
                        onClick={() => loadLineItemPage(lineItemsOffset - LINE_ITEM_RENDER_LIMIT)}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={detailsLoading || !hasNextLineItemPage}
                        onClick={() => loadLineItemPage(lineItemsOffset + LINE_ITEM_RENDER_LIMIT)}
                      >
                        Next
                      </Button>
                    </>
                  ) : null}

                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<RefreshIcon />}
                    disabled={detailsLoading || !activeSheet?.["Cost Sheet ID"]}
                    onClick={() =>
                      loadLineItemsForSheet(activeSheet?.["Cost Sheet ID"], {
                        force: lineItemsRequested,
                        offset: lineItemsRequested ? lineItemsOffset : 0,
                      })
                    }
                  >
                    {lineItemsRequested ? "Refresh" : "Load / Edit Line Items"}
                  </Button>

                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon />}
                    sx={{ bgcolor: cornflowerBlue }}
                    onClick={() => openDrawerAdd("")}
                  >
                    Add Line Item
                  </Button>
                </Box>
              </Box>
            </Paper>

            {!lineItemsRequested ? (
              <Paper sx={{ p: 2, borderRadius: 2, border: "1px solid #eee" }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: { xs: "stretch", sm: "center" },
                    justifyContent: "space-between",
                    gap: 1.5,
                    flexWrap: "wrap",
                  }}
                >
                  <Box sx={{ flex: "1 1 320px" }}>
                    <Typography sx={{ fontWeight: 900, fontSize: 13, mb: 0.6 }}>
                      Line items are loaded only when needed
                    </Typography>
                    <Typography sx={{ fontSize: 12, opacity: 0.72 }}>
                      This keeps the cost sheet modal fast. Add a new line item directly, or load line items when you
                      need to view, edit, or delete existing entries.
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={<RefreshIcon />}
                    sx={{ bgcolor: cornflowerBlue, alignSelf: { xs: "stretch", sm: "center" } }}
                    disabled={detailsLoading || !activeSheet?.["Cost Sheet ID"]}
                    onClick={() => loadLineItemsForSheet(activeSheet?.["Cost Sheet ID"])}
                  >
                    Load / Edit Line Items
                  </Button>
                </Box>
              </Paper>
            ) : (
              <Paper sx={{ borderRadius: 2, border: "1px solid #eee", overflow: "hidden" }}>
                <TableContainer sx={{ maxHeight: 520 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow sx={{ background: "#f6f9ff" }}>
                        {lineItemCols.map((c) => (
                          <TableCell key={c} sx={{ fontWeight: 900, fontSize: 11, whiteSpace: "nowrap" }}>
                            {c}
                          </TableCell>
                        ))}
                        <TableCell sx={{ fontWeight: 900, fontSize: 11, whiteSpace: "nowrap" }}>
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {detailsLoading ? (
                        <TableRow>
                          <TableCell colSpan={lineItemCols.length + 1} sx={{ fontSize: 12, opacity: 0.7 }}>
                            Loading line items…
                          </TableCell>
                        </TableRow>
                      ) : null}

                      {!detailsLoading &&
                        visibleLineItems.map((it, idx) => (
                          <TableRow key={it.__lineKey || idx} hover>
                            {lineItemCols.map((c) => (
                              <TableCell key={c} sx={{ fontSize: 11, verticalAlign: "top" }}>
                                {formatDateCell(c, it[c])}
                              </TableCell>
                            ))}
                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              <IconButton
                                title="Edit (will mark old row inactive and append updated row)"
                                onClick={() => openDrawerEdit(it)}
                                disabled={loading}
                              >
                                <EditIcon sx={{ color: cornflowerBlue }} />
                              </IconButton>

                              <IconButton
                                onClick={() => softDeleteLineItem(it)}
                                disabled={loading}
                                title="Delete"
                              >
                                <DeleteOutlineIcon sx={{ color: "#c62828" }} />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}

                      {!detailsLoading && hiddenLineItemCount ? (
                        <TableRow>
                          <TableCell colSpan={lineItemCols.length + 1} sx={{ fontSize: 12, opacity: 0.72 }}>
                            {hiddenLineItemCount} more matching line item(s) are not loaded in this fast editor view.
                            Use Next to view the next page.
                          </TableCell>
                        </TableRow>
                      ) : null}

                      {!detailsLoading && !lineItems.length ? (
                        <TableRow>
                          <TableCell colSpan={lineItemCols.length + 1} sx={{ fontSize: 12, opacity: 0.7 }}>
                            No line items found for this cost sheet.
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}

            {/* RIGHT DRAWER */}
            <Drawer
              anchor="right"
              open={drawerOpen}
              onClose={(_, reason) => reason !== "backdropClick" && closeDrawer()}
              sx={{ zIndex: (t) => (t?.zIndex?.modal ?? 1300) + 20 }}
              ModalProps={{ keepMounted: true }}
              PaperProps={{
                ref: drawerPaperRef,
                sx: {
                  width: { xs: "100%", sm: 420 },
                  p: 2,
                  zIndex: (t) => (t?.zIndex?.modal ?? 1300) + 21,
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                <Typography sx={{ fontWeight: 900, fontSize: 16 }}>
                  {drawerMode === "edit" ? "Edit Line Item" : "Add Line Item"}
                </Typography>

                <IconButton onClick={closeDrawer}>
                  <CloseIcon />
                </IconButton>
              </Box>

              <Typography sx={{ fontSize: 11, opacity: 0.75, mb: 1 }}>
                Shortcuts: Ctrl+Enter (Save) • Ctrl+Shift+Enter (Save & New) • Esc (Close)
              </Typography>

              <Divider sx={{ mb: 1.5 }} />

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Head Name</InputLabel>
                  <Select
                    label="Head Name"
                    value={drawerDraft["Head Name"] || ""}
                    onChange={(e) => setDrawerField("Head Name", e.target.value)}
                    MenuProps={getDrawerMenuProps()}
                  >
                    {(validation.heads || []).map((h) => (
                      <MenuItem key={h} value={h}>
                        {h}
                      </MenuItem>
                    ))}
                    {!validation.heads?.length ? (
                      <MenuItem value="" disabled>
                        No heads found
                      </MenuItem>
                    ) : null}
                  </Select>
                </FormControl>

                <FormControl size="small" fullWidth>
                  <InputLabel>Subcategory</InputLabel>
                  <Select
                    label="Subcategory"
                    value={drawerDraft.Subcategory || ""}
                    onChange={(e) => setDrawerField("Subcategory", e.target.value)}
                    MenuProps={getDrawerMenuProps()}
                  >
                    {subcatsForHead(drawerDraft["Head Name"] || "").map((s) => (
                      <MenuItem key={s} value={s}>
                        {s}
                      </MenuItem>
                    ))}
                    {!subcatsForHead(drawerDraft["Head Name"] || "").length ? (
                      <MenuItem value="" disabled>
                        No subcategories
                      </MenuItem>
                    ) : null}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  size="small"
                  label="Expense Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={drawerDraft["Expense Date"] || ""}
                  onChange={(e) => setDrawerField("Expense Date", e.target.value)}
                />

                <TextField
                  fullWidth
                  size="small"
                  label="Entry Tag"
                  value={drawerDraft["Entry Tag"] || ""}
                  onChange={(e) => setDrawerField("Entry Tag", e.target.value)}
                />

                <FormControl size="small" fullWidth>
                  <InputLabel>Payment Status</InputLabel>
                  <Select
                    label="Payment Status"
                    value={drawerDraft["Payment Status"] || ""}
                    onChange={(e) => setDrawerField("Payment Status", e.target.value)}
                    MenuProps={getDrawerMenuProps()}
                  >
                    {(validation.paymentStatus || DEFAULT_PAYMENT_STATUSES).map((s) => (
                      <MenuItem key={s} value={s}>
                        {s}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  size="small"
                  label="Particular"
                  value={drawerDraft.Particular || ""}
                  onChange={(e) => setDrawerField("Particular", e.target.value)}
                />

                <TextField
                  fullWidth
                  size="small"
                  label="Details"
                  value={drawerDraft.Details || ""}
                  onChange={(e) => setDrawerField("Details", e.target.value)}
                  multiline
                  minRows={2}
                />

                <Grid container spacing={1}>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="QTY"
                      value={drawerDraft["QTY"] || ""}
                      onChange={(e) => setDrawerField("QTY", e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Rate"
                      value={drawerDraft["Rate"] || ""}
                      onChange={(e) => setDrawerField("Rate", e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="GST %"
                      value={drawerDraft["GST %"] || ""}
                      onChange={(e) => setDrawerField("GST %", e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={Boolean(drawerDraft.__useAutoAmount) && Boolean(drawerDraft.__hasQtyRate)}
                          disabled={!drawerDraft.__hasQtyRate}
                          onChange={(e) => setDrawerField("__useAutoAmount", e.target.checked)}
                        />
                      }
                      label={<Typography sx={{ fontSize: 12 }}>Use Auto Amount (QTY × Rate)</Typography>}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Amount"
                      value={drawerDraft["Amount"] || ""}
                      onChange={(e) => setDrawerField("Amount", e.target.value)}
                      InputProps={{
                        readOnly: Boolean(drawerDraft.__useAutoAmount) && Boolean(drawerDraft.__hasQtyRate),
                      }}
                      helperText={
                        drawerDraft.__hasQtyRate
                          ? drawerDraft.__useAutoAmount
                            ? "Auto (QTY × Rate)"
                            : "Manual"
                          : drawerDraft.__useManualTotal
                          ? "Derived from Total Amount"
                          : "Manual"
                      }
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="GST Amount"
                      value={drawerDraft["GST Amount"] || ""}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Total Amount"
                      value={drawerDraft["Total Amount"] || ""}
                      onChange={(e) => setDrawerField("Total Amount", e.target.value)}
                      InputProps={{ readOnly: Boolean(drawerDraft.__hasQtyRate) }}
                      helperText={
                        drawerDraft.__hasQtyRate
                          ? "Auto (Amount + GST Amount)"
                          : "Manual Total (when QTY×Rate is unavailable)"
                      }
                    />
                  </Grid>
                </Grid>

                <TextField
                  fullWidth
                  size="small"
                  label="Attachment Link"
                  value={drawerDraft["Attachment Link"] || ""}
                  onChange={(e) => setDrawerField("Attachment Link", e.target.value)}
                />

                <TextField
                  fullWidth
                  size="small"
                  label="Voucher/Invoice No"
                  value={drawerDraft["Voucher/Invoice No"] || ""}
                  onChange={(e) => setDrawerField("Voucher/Invoice No", e.target.value)}
                />

                <Divider sx={{ my: 0.5 }} />

                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button variant="outlined" fullWidth onClick={closeDrawer} disabled={loading}>
                    Cancel
                  </Button>

                  <Button
                    variant="contained"
                    fullWidth
                    sx={{ bgcolor: cornflowerBlue }}
                    onClick={saveDrawer}
                    disabled={loading}
                  >
                    {loading ? "Saving…" : "Save"}
                  </Button>
                </Box>

                <Button
                  variant="contained"
                  fullWidth
                  sx={{ bgcolor: "#1f2a44" }}
                  onClick={saveDrawerAndNew}
                  disabled={loading}
                >
                  {loading ? "Saving…" : "Save & New"}
                </Button>

                {drawerMode === "edit" ? (
                  <Typography sx={{ fontSize: 11, opacity: 0.75 }}>
                    Note: Edit marks the old row inactive and appends the updated row (audit-safe). The UI removes the
                    old row using a stable key to avoid duplicates.
                  </Typography>
                ) : null}
              </Box>
            </Drawer>
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setOpenEdit(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ThemeProvider>
  );
}
